/**
 * DAS LIEFERANTENPORTAL -- Liste und Akte.
 *
 * WAS DIESER BEREICH IST
 *
 * Die Akte des Geschaeftspartners. Sie beantwortet in wenigen Sekunden:
 * Wer ist das, was kommt von dort, wie beurteilen wir ihn, was ist
 * vereinbart -- und was fehlt.
 *
 * WAS SIE NICHT IST
 *
 * Keine zweite Produktdatenbank. Die Artikel kommen aus
 * `supplier_articles` und werden hier GEZEIGT, nicht gehalten. Es
 * entsteht keine zweite Supplier-Artikel-Beziehung, und ein Artikel darf
 * weiterhin mehrere Lieferanten haben -- die Produktliste sagt es sogar
 * dazu.
 *
 * FOUNDATION V1 -- WAS BEWUSST FEHLT
 *
 * Qualitaet/QC, Wissensluecken, Vorgaenge, Dokumente und Bankdaten sind
 * fachlich vorgesehen und hier NICHT gebaut. Sie erscheinen auch nicht
 * als leere Reiter: ein Bereich, der nichts kann, ist ein Versprechen.
 * Die Bereichsliste steht in `SUPPLIER_SECTIONS` -- ein weiterer Bereich
 * ist dort eine Zeile.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  PaymentMethod,
  ContactChannel, MessengerKind, MetaResponse, PartnerRoleInfo, SupplierArticleRow,
  SupplierBankAccount, SupplierContact, SupplierNote,
  UpsertSupplierBankAccountRequest,
  SupplierContactRole, SupplierDetail,
  SupplierListItem, SupplierNeedQuestion, SupplierRequirements,
  SupplierSectionCode, UpsertSupplierContactRequest,
} from "@homebase/shared";
import {
  CONTACT_CHANNELS, SUPPLIER_CONTACT_ROLES, SUPPLIER_SECTIONS,
  SUPPLIER_SECTION_CAPABILITY, SUPPLIER_ONLY_SECTIONS, MESSENGER_KINDS,
  SUPPLIER_STATUSES, COUNTRY_CODES,
} from "@homebase/shared";
import type { SupplierStatus } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { propertyLabel } from "../i18n/terminology.ts";
import { zeigeUndFokussiere } from "../lib/fokus.ts";
import { en } from "../i18n/en.ts";
import { ProductSubnav } from "./ProductSubnav.tsx";
import {
  BEWERTUNGSFELDER, ausBewertung, bewertungGleich, BewertungsBox,
  ausZahlplan, zahlplanGleich, zahlplanSatz, Zahlplaneditor,
  type Bewertung, type Zahlentwurf,
} from "./SupplierBits.tsx";
import "./SupplierPortal.css";

const PENCIL = "✎";

/**
 * DER LANDERNAME KOMMT VOM BROWSER.
 *
 * Gespeichert wird der Code nach ISO 3166-1 (CN, DE, …) -- so verlangt
 * es die Datenbank seit 0005, und so ist es international richtig.
 * Angezeigt wird der Name in der Sprache des Benutzers; 249 Laendernamen
 * je Sprache im Woerterbuch waeren derselbe Inhalt zweimal.
 */
function landName(code: string, lang: string): string {
  try {
    return new Intl.DisplayNames([lang], { type: "region" }).of(code) ?? code;
  } catch { return code; }
}

/** Die Laender, nach ihrem Namen in der aktuellen Sprache sortiert. */
function laenderListe(lang: string): ReadonlyArray<{ code: string; name: string }> {
  return COUNTRY_CODES.map((code) => ({ code, name: landName(code, lang) }))
    .sort((a, b) => a.name.localeCompare(b.name, lang));
}

/** Eine Statusmarke -- sachlich, nur „inaktiv" bekommt einen Warnton. */
function StatusMarke({ status, i18n }: { status: SupplierStatus; i18n: I18n }): React.JSX.Element {
  return (
    <span className={status === "inactive" ? "sup__tag sup__tag--aus" : "sup__tag"}>
      {i18n.tCode("supStatus", status)}
    </span>
  );
}

/** „nicht bestimmt" -- dieselbe Auskunft wie ueberall in HOMEBASE. */
/**
 * DIE ROLLEN EINER ORGANISATION -- in der Ordnung des Katalogs.
 *
 * Sie stehen als Marken da und nicht als Satz: „Lieferant · Forwarder"
 * ist auf einen Blick lesbar, und dass es MEHRERE sein koennen, sieht
 * man sofort. Eine Auswahlliste haette das verborgen.
 */
function Rollen({ rollen, i18n }: {
  rollen: readonly string[]; i18n: I18n;
}): React.JSX.Element {
  return (
    <span className="sup__rollen" data-testid="sup-rollen">
      {rollen.map((r) => (
        <span key={r} className="sup__tag sup__tag--rolle" data-rolle={r}>
          {i18n.tCode("partnerRole", r, r)}
        </span>
      ))}
    </span>
  );
}

function Unknown(): React.JSX.Element {
  const { t } = useI18n();
  return <span className="detail__unknown">{t("common.unknownValue")}</span>;
}

/** Eine Zeile in einer Box: Beschriftung links, Wert rechts. */
function Zeile({ label, children, ziel }: {
  label: string; children: React.ReactNode;
  /** Kennung fuer den Sprung aus der Uebersicht -- siehe `Stammdaten`. */
  ziel?: string;
}): React.JSX.Element {
  return (
    <div className="log__item" data-ziel={ziel}>
      <span className="log__label">{label}</span>
      <span className="log__vals">{children}</span>
    </div>
  );
}

function Box({ titel, children, klasse }: {
  titel: string; children: React.ReactNode; klasse?: string;
}): React.JSX.Element {
  return (
    <section className={klasse ? `log__group ${klasse}` : "log__group"}>
      <h4 className="log__grouptitle">{titel}</h4>
      <div className="log__rows">{children}</div>
    </section>
  );
}

export function SupplierPortal({ meta, capabilities, openId, onOpen, onOpenArticle, fokus,
                                onPartnersChanged }: {
  meta: MetaResponse;
  capabilities: readonly string[];
  /*
   * EIN GEAENDERTER PARTNERBESTAND GEHT ANDERE BEREICHE AN.
   *
   * DER BEFUND AUS DER BROWSER-ABNAHME (BP/LOG-03): GROUP7 wurde hier
   * mit der Rolle Forwarder angelegt, und die Sendungsakte sagte
   * weiterhin „Noch kein Geschaeftspartner traegt die Rolle Forwarder".
   * Erst ein Neuladen des Browsers half.
   *
   * DIE URSACHE WAR NICHT DIE SENDUNGSAKTE. Sie liest `meta.partners`
   * -- und dieses Portal war der einzige Bereich, der dem Rahmen eine
   * Aenderung NIE meldete. Die Artikelansicht, die Struktur und die
   * Modelle tun es seit jeher; hier fehlte die Zeile.
   *
   * Gemeldet wird nach allem, was den Bestand oder die Rollen aendert:
   * anlegen, Stammdaten speichern, zusammenfuehren. Der Rahmen holt
   * `meta` dann ohne Flackern nach -- `refreshMeta`, nicht `loadMeta`.
   */
  onPartnersChanged?: () => void;
  /*
   * Der geoeffnete Lieferant -- vom Rahmen gehalten, damit die Produktakte
   * mit „Lieferant öffnen" direkt zu IHM fuehren kann (wie bei den
   * Produktmodellen). Ohne Angabe fuehrt das Portal ihn selbst.
   */
  openId?: number | null;
  onOpen?: (id: number | null) => void;
  /*
   * Der Weg in die PRODUKTAKTE -- derselbe, den die Produktmodelle
   * schon benutzen. Ein Klick auf einen Artikel fuehrt dorthin, wo der
   * Artikel zu Hause ist, und nicht in eine Lieferantenkopie seiner
   * Daten.
   */
  onOpenArticle: (articleId: number) => void;
  /* „bewertung": die Akte oeffnet in den Konditionen, an der Bewertung. */
  fokus?: "bewertung" | null;
}): React.JSX.Element {
  const [eigen, setEigen] = useState<number | null>(null);
  const offen = openId !== undefined ? openId : eigen;
  const setOffen = onOpen ?? setEigen;
  const gemeldet = onPartnersChanged ?? ((): void => {});
  return offen === null
    ? <SupplierList meta={meta} onOpen={setOffen}
        mayWrite={capabilities.includes("supplier.write")}
        onPartnersChanged={gemeldet} />
    : <SupplierFile key={offen} id={offen} meta={meta} capabilities={capabilities} fokus={fokus ?? null}
        onOpenArticle={onOpenArticle} onClose={() => setOffen(null)}
        onPartnersChanged={gemeldet} />;
}

// ── Die Liste ───────────────────────────────────────────────────────────────

function SupplierList({ meta, onOpen, mayWrite, onPartnersChanged }: {
  onPartnersChanged: () => void;
  meta: MetaResponse; onOpen: (id: number) => void; mayWrite: boolean;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [items, setItems] = useState<readonly SupplierListItem[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [suche, setSuche] = useState("");
  const [rolle, setRolle] = useState<string>("");
  const [anlegen, setAnlegen] = useState(false);

  /*
   * DER FILTER LIEGT AM SERVER, nicht in dieser Liste.
   *
   * Ein Filter im Browser waere eine zweite Vorstellung davon, wer
   * Lieferant ist -- und die Liste, die HOMEBASE ausliefert, waere nicht
   * mehr die, die HOMEBASE meint.
   */
  const laden = useCallback(async () => {
    try {
      setItems((await api.suppliers(rolle === "" ? undefined : rolle)).items);
    } catch (e) { setError(e); }
  }, [rolle]);
  useEffect(() => { void laden(); }, [laden]);

  /*
   * DIE LISTE ZEIGT NUR AKTIVE, NICHT ZUSAMMENGEFUEHRTE PARTNER.
   *
   * Der Server liefert bereits keine Grabsteine aus (0039/0040) -- hier
   * steht deshalb keine zweite Filterung, die dasselbe noch einmal
   * behauptet. Was „inaktiv" ist, bleibt sichtbar und wird als solches
   * gekennzeichnet: ein Partner, mit dem man gerade nicht arbeitet, ist
   * kein Partner, den man versteckt.
   */
  const gefiltert = (items ?? []).filter((s) => {
    const q = suche.trim().toLowerCase();
    if (q === "") return true;
    return s.name.toLowerCase().includes(q)
        || (s.legalName ?? "").toLowerCase().includes(q);
  });

  return (
    <div className="sup">
      <div className="sup__kopf">
        <h2 className="sup__titel">{t("sup.title")}</h2>
        <input className="list__search" value={suche} aria-label={t("sup.search")}
          placeholder={t("sup.search")} onChange={(e) => setSuche(e.target.value)} />
        {/*
          * WOFUER DER PARTNER STEHT -- als Filter, nicht als Kartei.
          *
          * Es gibt EINE Geschaeftspartnerliste. Wer nur Lieferanten
          * sehen will, schraenkt sie ein; er wechselt nicht in eine
          * andere Kartei, in der dieselbe Firma ein zweites Mal steht.
          */}
        <span className="sup__rollenfilter" role="group" aria-label={t("sup.roleFilter")}>
          {[{ code: "", label: t("sup.roleAll") },
            ...meta.partnerRoles.map((r) => ({
              code: r.code, label: i18n.tCode("partnerRole", r.code, r.code) }))]
            .map((r) => (
              <button key={r.code || "alle"} type="button" data-testid="sup-rollenfilter"
                data-rolle={r.code} aria-pressed={rolle === r.code}
                className={rolle === r.code ? "sdb__sichtknopf sdb__sichtknopf--on" : "sdb__sichtknopf"}
                onClick={() => setRolle(r.code)}>{r.label}</button>
            ))}
        </span>
        <span className="sup__zahl">
          {items === null ? t("common.loading") : t("sup.count", { n: gefiltert.length })}
        </span>
        {/*
          * DIE ANLAGE STEHT DORT, WO MAN SIE SUCHT -- am Kopf der Liste,
          * wie das Plus in der Artikelliste. Ein Partner entsteht hier
          * und nicht als Nebenwirkung eines Artikels.
          */}
        {mayWrite && (
          <button className="btn btn--primary btn--small sup__plus"
            onClick={() => setAnlegen(true)}>{t("sup.add")}</button>
        )}
      </div>
      {error !== null && <p className="log__error">{errorText(i18n, error)}</p>}
      {anlegen && (
        <NeuerLieferant i18n={i18n} meta={meta} onAbbruch={() => setAnlegen(false)}
          onAngelegt={(id) => {
            setAnlegen(false); void laden(); onPartnersChanged(); onOpen(id); }}
          onError={setError} />
      )}
      {items !== null && (
        <div className="sup__tablewrap">
          <table className="sup__table">
            <thead>
              <tr>
                <th>{t("sup.name")}</th>
                <th>{t("sup.legalName")}</th>
                <th>{t("sup.roles")}</th>
                <th className="sup__num">{t("sup.articles")}</th>
                <th className="sup__num">{t("sup.rating")}</th>
                <th>{t("sup.state")}</th>
              </tr>
            </thead>
            <tbody>
              {gefiltert.map((s) => (
                <tr key={s.id} className="sup__row" tabIndex={0} role="button"
                    onClick={() => onOpen(s.id)}
                    onKeyDown={(e) => { if (e.key === "Enter") onOpen(s.id); }}>
                  <td className="sup__kurz">{s.name}</td>
                  <td>{s.legalName ?? <Unknown />}</td>
                  <td><Rollen rollen={s.roles} i18n={i18n} /></td>
                  {/* Die Artikelzahl meint Lieferantenartikel. Bei einem
                      Partner ohne Lieferantenrolle steht dort nichts --
                      eine 0 waere eine Aussage ueber ein Sortiment, das
                      es bei ihm gar nicht gibt. */}
                  <td className="sup__num">
                    {s.roles.includes("lieferant") ? s.articleCount : <Unknown />}
                  </td>
                  {/* Die Gesamtnote ist gerechnet. Ohne Bewertung steht
                      dort nichts -- und ausdruecklich keine 0. */}
                  <td className="sup__num">
                    {s.ratingOverall === null ? <Unknown />
                      : Number(s.ratingOverall).toLocaleString(i18n.lang,
                          { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                  </td>
                  <td>
                    <StatusMarke status={s.status} i18n={i18n} />
                    {/*
                      * WAS FEHLT, IST ANKLICKBAR -- und nennt beim Namen,
                      * worum es geht: fehlende SUPPLIER-STAMMDATEN. Ueber
                      * Produktwissen sagt diese Zahl nichts; dafuer gibt
                      * es noch kein Modell.
                      */}
                    {s.missing.length > 0 && (
                      <button type="button" className="sup__tag sup__tag--klick"
                        onClick={(e) => { e.stopPropagation(); onOpen(s.id); }}
                        title={s.missing.map((m) => i18n.tCode("supMissing", m, m)).join(", ")}>
                        {t("sup.missing", { n: s.missing.length })}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Ein neuer Lieferant -- drei Angaben, mehr nicht.
 *
 * Alles Weitere gehoert in die Akte. Ein Anlageformular, das zwanzig
 * Felder verlangt, verhindert das Anlegen -- und gerade ein Partner im
 * Sourcing hat die meisten davon noch gar nicht.
 */
function NeuerLieferant({ i18n, meta, onAngelegt, onAbbruch, onError }: {
  i18n: I18n; meta: MetaResponse; onAngelegt: (id: number) => void; onAbbruch: () => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [name, setName] = useState("");
  const [firma, setFirma] = useState("");
  const [status, setStatus] = useState<SupplierStatus>("active");
  /*
   * VORAUSGEWAEHLT IST LIEFERANT.
   *
   * Das ist der haeufigste Fall und war bis zu dieser Fassung der
   * einzige. Ein leeres Kaestchenfeld haette jeden gezwungen, das
   * Offensichtliche zu bestaetigen.
   */
  const [rollen, setRollen] = useState<readonly string[]>(["lieferant"]);
  const [speichert, setSpeichert] = useState(false);

  async function anlegen(): Promise<void> {
    setSpeichert(true);
    try {
      const neu = await api.createSupplier({
        name, legalName: firma.trim() === "" ? null : firma.trim(), status, roles: rollen,
      });
      onAngelegt(neu.id);
    } catch (e) { onError(e); } finally { setSpeichert(false); }
  }

  return (
    <section className="log__group sup__anlage">
      <h4 className="log__grouptitle">{t("sup.addTitle")}</h4>
      <div className="sup__kform">
        {/* Der Kurzname steht in VERSALIEN -- das Feld zeigt es, statt
            es hinterher stillschweigend zu tun. */}
        <input className="log__input log__input--text sup__eingabe" value={name}
          aria-label={t("sup.shortName")} placeholder={t("sup.shortName")}
          disabled={speichert}
          onChange={(e) => setName(e.target.value.toUpperCase())} />
        {/* Keine Firmierung wird aus dem Kurznamen gebildet. Wer keine
            kennt, laesst das Feld leer. */}
        <input className="log__input log__input--text sup__eingabe--breit" value={firma}
          aria-label={t("sup.legalName")} placeholder={t("sup.legalNameOptional")}
          disabled={speichert} onChange={(e) => setFirma(e.target.value)} />
        <select className="log__input" value={status} disabled={speichert}
          aria-label={t("sup.status")}
          onChange={(e) => setStatus(e.target.value as SupplierStatus)}>
          {SUPPLIER_STATUSES.map((x) => (
            <option key={x} value={x}>{i18n.tCode("supStatus", x)}</option>
          ))}
        </select>
        {/* Mehrere Rollen sind der Normalfall: eine Spedition, die auch
            Verpackungsmaterial liefert, ist beides -- und nicht zweimal. */}
        <span className="sup__krollen" role="group" aria-label={t("sup.roles")}>
          {meta.partnerRoles.map((r) => (
            <label key={r.code} className="sup__schalter">
              <input type="checkbox" data-testid="sup-neurolle" data-rolle={r.code}
                checked={rollen.includes(r.code)} disabled={speichert}
                onChange={(e) => setRollen(e.target.checked
                  ? [...rollen, r.code]
                  : rollen.filter((x) => x !== r.code))} />
              {i18n.tCode("partnerRole", r.code, r.code)}
            </label>
          ))}
        </span>
        <button className="btn btn--primary btn--small"
          disabled={speichert || name.trim() === "" || rollen.length === 0}
          onClick={() => void anlegen()}>
          {speichert ? t("props.saving") : t("sup.addSave")}
        </button>
        <button className="btn btn--small" disabled={speichert}
          onClick={onAbbruch}>{t("common.cancel")}</button>
      </div>
      {/* Ein Partner braucht keinen Artikel, um zu existieren. */}
      <p className="log__hint">{t("sup.addHint")}</p>
    </section>
  );
}

// ── Die Akte ────────────────────────────────────────────────────────────────

function SupplierFile({ id, meta, capabilities, onOpenArticle, onClose, fokus,
                       onPartnersChanged }: {
  onPartnersChanged: () => void;
  id: number;
  fokus: "bewertung" | null;
  meta: MetaResponse;
  capabilities: readonly string[];
  onOpenArticle: (articleId: number) => void;
  onClose: () => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [daten, setDaten] = useState<SupplierDetail | null>(null);
  /*
   * ZWEI ARTEN VON FEHLER, UND SIE HABEN VERSCHIEDENE FOLGEN.
   *
   * Hier stand EIN Zustand fuer beides -- und jede Absage eines Kindes
   * ersetzte die ganze Akte durch einen Satz. Gemessen: ein
   * zurueckgewiesenes Speichern im Kontaktformular liess vom
   * Lieferanten NICHTS stehen, nicht einmal die Bereichsleiste. Nur die
   * Meldung „Ein weiterer Kontaktkanal braucht Art und Kennung". Der
   * Anwender sah eine kaputte Seite, wo er eine Eingabe haette
   * korrigieren sollen -- und kam nicht einmal mehr zum Formular
   * zurueck, weil die Reiter mitverschwunden waren.
   *
   *   ladeFehler     Die Akte konnte nicht geladen werden. Dann gibt es
   *                  wirklich nichts zu zeigen, und die Meldung steht
   *                  allein da.
   *   aktionsFehler  Eine HANDLUNG wurde abgewiesen. Die Akte steht
   *                  weiter; die Absage steht darueber, dort wo man
   *                  die Eingabe korrigiert.
   *
   * Dieselbe Unterscheidung, die die Produktakte seit Produktmodelle
   * 003 macht: „die Absage steht am Editor, die Reiter bleiben stehen".
   */
  const [ladeFehler, setLadeFehler] = useState<unknown>(null);
  const [aktionsFehler, setAktionsFehler] = useState<unknown>(null);
  const [bereich, setBereich] = useState<SupplierSectionCode>(fokus === "bewertung" ? "konditionen" : "uebersicht");
  const [bearbeitet, setBearbeitet] = useState(false);
  /* Welcher Ansprechpartner beim Einschalten der Bearbeitung geoeffnet
     werden soll -- `null`, wenn der Modus von Hand eingeschaltet wurde. */
  const [kontaktVorwahl, setKontaktVorwahl] = useState<number | null>(null);
  /* Wurde der Modus eigens fuer einen Kontakt eingeschaltet -- oder von
     Hand ueber den Stift? Davon haengt ab, ob ein Abbruch ihn wieder
     schliesst. */
  const [autoBearbeitet, setAutoBearbeitet] = useState(false);
  /*
   * WOHIN DIE UEBERSICHT GESCHICKT HAT -- und sonst nichts.
   *
   * Die Uebersicht bearbeitet nichts; sie zeigt eine Luecke und fuehrt
   * dorthin, wo man sie schliesst. Der Name der Fehlstelle ist der ganze
   * Zustand, den sie dafuer braucht. Er wird verbraucht, sobald das Ziel
   * ihn gelesen hat -- bliebe er stehen, spraenge die Maske bei jedem
   * Neuzeichnen erneut.
   */
  const [vorwahl, setVorwahl] = useState<string | null>(null);

  /* Ordnung in der Oberflaeche, KEIN Schutz -- die Daten schuetzt der
     Server. Ein Reiter, den es fuer diesen Nutzer nicht gibt, erscheint
     gar nicht erst; ausgegraut waere er bereits eine Auskunft. */
  /*
   * UND DIE BEREICHE, DIE ZU DIESER ORGANISATION GEHOEREN.
   *
   * Artikel, Datenbedarf und Konditionen sind lieferantenspezifisch. Ein
   * Forwarder bekommt dort keine leeren Tabellen und keine
   * Lieferantenbewertung ohne Lieferung -- er bekommt die Reiter gar
   * nicht. Welche Rolle sie oeffnet, sagt der Katalog
   * (`opensSupplierAreas`), nicht diese Datei.
   *
   * Solange die Akte laedt, bleibt es bei den Faehigkeiten: ein Reiter,
   * der beim Laden auftaucht und gleich wieder verschwindet, ist
   * schlimmer als einer, der einen Moment spaeter kommt.
   */
  const lieferantenbereiche = daten === null || meta.partnerRoles
    .filter((r) => r.opensSupplierAreas)
    .some((r) => daten.roles.includes(r.code));

  const sichtbareBereiche = useMemo(
    () => SUPPLIER_SECTIONS.filter((b) => {
      const noetig = SUPPLIER_SECTION_CAPABILITY[b];
      if (noetig !== undefined && !capabilities.includes(noetig)) return false;
      return lieferantenbereiche || !SUPPLIER_ONLY_SECTIONS.includes(b);
    }),
    [capabilities, lieferantenbereiche]);

  const mayWrite = capabilities.includes("supplier.write");
  const mayTerms = capabilities.includes("logistics.write");
  const mayConfirm = capabilities.includes("logistics.confirm");
  /*
   * Wo der Stift erscheint, und mit welchem Recht. Die Uebersicht und
   * die Produktliste zeigen nur -- dort gibt es nichts zu bearbeiten,
   * und ein Stift waere eine Ankuendigung.
   *
   * In den Konditionen haengen ZWEI Rechte: Zahlungsplan und Bewertung
   * an `logistics.write`, Lieferklausel und Waehrung an
   * `supplier.write`. Wer eines davon hat, bekommt den Stift -- und
   * sieht drinnen nur die Felder offen, die ihm gehoeren. Beides zu
   * einem Recht zusammenzufassen waere eine Rechteausweitung zur
   * Vereinfachung der Oberflaeche.
   */
  const darfBearbeiten = (bereich === "stammdaten" && mayWrite)
                      || (bereich === "konditionen" && (mayTerms || mayWrite));

  const laden = useCallback(async () => {
    /* Bei Erfolg wird der alte Ladefehler ZURUECKGENOMMEN. Ohne das
       bliebe eine einmalige Stoerung stehen, bis jemand die Seite neu
       laedt -- und die Akte waere dauerhaft eine Fehlermeldung. */
    try { setDaten(await api.supplier(id)); setLadeFehler(null); }
    catch (e) { setLadeFehler(e); }
  }, [id]);
  useEffect(() => { void laden(); }, [laden]);
  useEffect(() => { setBearbeitet(false); setAktionsFehler(null); }, [bereich, id]);
  /*
   * ... UND WIEDER AUF, WENN EIN SPRUNG HIERHER GEFUEHRT HAT.
   *
   * Der Effekt darueber schliesst die Bearbeitung bei jedem
   * Bereichswechsel, und das ist richtig: ein Stift, der in einem
   * anderen Bereich weiterleuchtet, verspricht etwas, das dort nicht
   * stattfindet. Ein Sprung aus der Uebersicht IST aber ein
   * Bereichswechsel mit genau der Absicht zu bearbeiten.
   *
   * Deshalb hier, NACH dem Schliessen, das Oeffnen -- und nur mit dem
   * Recht dazu. Wer `supplier.write` nicht hat, landet an der richtigen
   * Stelle und liest sie. Die Navigation ist kein Schluessel.
   */
  useEffect(() => {
    if (vorwahl === null) return;
    if (bereich === "stammdaten" && mayWrite) setBearbeitet(true);
    if (bereich === "konditionen" && (mayTerms || mayWrite)) setBearbeitet(true);
  }, [vorwahl, bereich, mayWrite, mayTerms]);
  /* Der Sprung selbst: Bereich wechseln und merken, worum es ging. Mehr
     tut er nicht -- kein Schreiben, kein Recht, keine Datenaenderung.
     Der Bereich kommt MIT: seit die Rahmenbedingungen in den Konditionen
     stehen, fuehren nicht mehr alle Luecken in die Stammdaten. */
  const vorwahlVerbraucht = useCallback(() => setVorwahl(null), []);
  const springeZu = useCallback((ziel: SupplierSectionCode, feld: string | null): void => {
    setBereich(ziel);
    setVorwahl(feld);
  }, []);
  /*
   * DIE ZAHL DES OFFENEN DATENBEDARFS -- geholt, nicht gerechnet.
   *
   * Die Uebersicht zeigt sie im Kasten Datenqualitaet. Sie kommt aus
   * derselben Antwort, die der Bereich Datenbedarf auswertet; eine
   * zweite Rechnung waere eine zweite Wahrheit ueber dieselbe Frage.
   *
   * SIE HAELT NICHTS AUF. Bei einem Partner mit neunzig Produkten
   * braucht die Antwort spuerbar Zeit -- die Akte steht trotzdem sofort
   * da, und die Zeile sagt so lange, dass gerechnet wird. Ein Ladebalken
   * ueber der ganzen Uebersicht waere der Fehler.
   */
  const [bedarf, setBedarf] = useState<SupplierRequirements | null>(null);
  useEffect(() => {
    let aktuell = true;
    setBedarf(null);
    void api.supplierRequirements(id)
      .then((r) => { if (aktuell) setBedarf(r); })
      /* Bleibt sie aus, bleibt die Zeile bei „wird gerechnet". Eine
         fehlgeschlagene Nebenauskunft darf die Akte nicht stoeren. */
      .catch(() => undefined);
    return () => { aktuell = false; };
  }, [id]);

  /* Aus dem Einkauf kommend: bis an die Bewertung, einmal, sobald sie steht. */
  const fokusErledigt = useRef(false);
  useEffect(() => {
    if (fokus !== "bewertung" || daten === null || fokusErledigt.current) return;
    fokusErledigt.current = true;
    document.querySelector(".sup .bew")?.scrollIntoView({ block: "start" });
  }, [fokus, daten]);

  /* NUR ein Ladefehler laesst die Akte verschwinden -- dann gibt es sie
     wirklich nicht. Alles andere steht ueber einer stehenden Akte. */
  if (ladeFehler !== null) return <p className="log__error">{errorText(i18n, ladeFehler)}</p>;
  if (daten === null) return <p className="work__empty">{t("common.loading")}</p>;

  return (
    <div className="sup">
      <header className="sup__akte">
        <button className="btn sup__zurueck" onClick={onClose}>{t("sup.back")}</button>
        <div className="sup__ident">
          {/* Der Kurzname steht gross, die Firmierung darunter. Sie sind
              zwei Angaben und werden nicht ineinander gerechnet. */}
          <h2 className="sup__kurzname">{daten.name}</h2>
          <span className="sup__firma">{daten.legalName ?? <Unknown />}</span>
        </div>
        <StatusMarke status={daten.status} i18n={i18n} />
      </header>

      {/*
        * DIE BEREICHE, DIE DIESER NUTZER SEHEN SOLL.
        *
        * Dieselbe Mechanik wie in der Produktakte: die Leiste kennt
        * keinen Bereichsnamen, die Sichtbarkeit kommt aus den
        * Faehigkeiten. Heute filtert das nichts weg -- und genau
        * deshalb steht es hier: der geschuetzte Bereich Finanzen /
        * Bankverbindungen ist damit spaeter zwei Zeilen in der
        * Deklaration und kein Umbau dieser Leiste.
        */}
      <ProductSubnav<SupplierSectionCode>
        sections={sichtbareBereiche} current={bereich} onSelect={(b) => { setBereich(b); setVorwahl(null); }} prefix="sup"
        /*
          * DERSELBE STIFT AN DERSELBEN STELLE -- fuer jeden Bereich, in
          * dem es etwas zu bearbeiten gibt. Bis V1.1 trug ihn nur der
          * Stammdatenbereich, waehrend die Konditionen ihre Felder
          * dauerhaft offen zeigten: zwei Bedienmuster fuer dieselbe
          * Sache. Welches Recht noetig ist, entscheidet der Bereich.
          */
        action={darfBearbeiten ? (
          <button type="button"
            className={bearbeitet ? "subnav__pencil subnav__pencil--on" : "subnav__pencil"}
            aria-pressed={bearbeitet} title={t("props.edit")}
            aria-label={t(bereich === "konditionen" ? "sup.editTermsAria" : "sup.editAria")}
            onClick={() => { setAutoBearbeitet(false); setBearbeitet(!bearbeitet); }}>
            {PENCIL}</button>
        ) : undefined} />

      <div className="detail__content">
        {/*
          * DIE ABSAGE STEHT UEBER DER AKTE, NICHT AN IHRER STELLE.
          *
          * Sie gehoert dorthin, wo man die Eingabe korrigiert -- also in
          * denselben Bereich, in dem sie entstanden ist. Ein Wegklicken
          * braucht sie nicht: der naechste Bereichswechsel und der
          * naechste gelungene Versuch nehmen sie von selbst zurueck.
          */}
        {aktionsFehler !== null && (
          <p className="log__error" role="alert" data-testid="sup-aktionsfehler">
            {errorText(i18n, aktionsFehler)}
          </p>
        )}
        {bereich === "uebersicht" && (
          <Uebersicht d={daten} i18n={i18n} lieferant={lieferantenbereiche}
            bedarf={bedarf} onSpringe={springeZu} />
        )}
        {bereich === "stammdaten" && (
          <Stammdaten d={daten} i18n={i18n} rollenKatalog={meta.partnerRoles}
            bearbeitet={bearbeitet} mayWrite={mayWrite}
            /*
             * EIN KLICK STATT DREI -- und ohne zweite Bearbeitungslogik.
             *
             * Der Befund aus der Abnahme: im Lesemodus war ein
             * Ansprechpartner nicht anklickbar. Man musste erst den
             * Stift der Stammdaten druecken, dann erschien je Kontakt
             * ein eigener Stift, und erst der oeffnete die Maske.
             *
             * Hier wird nichts Neues gebaut: derselbe Modus wird
             * eingeschaltet und derselbe Kontakt vorgewaehlt, den der
             * Anwender angeklickt hat. Es gibt weiterhin genau EINEN
             * Bearbeitungszustand.
             */
            kontaktVorwahl={kontaktVorwahl}
            autoBearbeitet={autoBearbeitet}
            onKontaktBearbeiten={(kid) => {
              setKontaktVorwahl(kid); setAutoBearbeitet(true); setBearbeitet(true);
            }}
            onKontaktVorwahlVerbraucht={() => setKontaktVorwahl(null)}
            vorwahl={vorwahl} onVerbraucht={vorwahlVerbraucht}
            onFertig={() => {
              setBearbeitet(false); setVorwahl(null); setKontaktVorwahl(null);
              setAutoBearbeitet(false); setAktionsFehler(null); void laden();
              /* Name, Firmierung und vor allem die ROLLEN koennen sich
                 geaendert haben -- und davon haengen Auswahlfelder in
                 anderen Bereichen ab. Siehe `onPartnersChanged`. */
              onPartnersChanged();
            }}
            onReload={() => { setAktionsFehler(null); void laden(); }}
            onError={setAktionsFehler} />
        )}
        {bereich === "produkte" && <Produkte id={id} i18n={i18n} onOpenArticle={onOpenArticle} />}
        {bereich === "datenbedarf" && (
          <Datenbedarf id={id} i18n={i18n} meta={meta} onOpenArticle={onOpenArticle} />
        )}
        {bereich === "konditionen" && (
          <Konditionen d={daten} meta={meta} i18n={i18n}
            bearbeitet={bearbeitet} mayTerms={mayTerms} mayWrite={mayWrite}
            mayConfirm={mayConfirm}
            vorwahl={vorwahl} onVerbraucht={vorwahlVerbraucht}
            onFertig={() => {
              setBearbeitet(false); setVorwahl(null); setAktionsFehler(null); void laden();
            }}
            onReload={() => { setAktionsFehler(null); void laden(); }}
            onError={setAktionsFehler} />
        )}
        {/*
          * FINANZEN -- ein eigener Bereich, seit Implementierung 003
          * vollstaendig benutzbar. Er hat KEINEN Stift in der
          * Bereichsleiste: jede Bankverbindung traegt ihren eigenen,
          * weil eine Sammelbearbeitung von null bis vielen Karten
          * keine ist.
          */}
        {bereich === "finanzen" && (
          <Finanzen d={daten} meta={meta} i18n={i18n} mayWrite={mayWrite}
            onChanged={() => { setAktionsFehler(null); void laden(); }}
            onError={setAktionsFehler} />
        )}
      </div>
    </div>
  );
}

// ── Übersicht ───────────────────────────────────────────────────────────────

/**
 * Der Name eines Ansprechpartners -- so, wie man ihn anspricht.
 *
 * Der Server liefert ihn bereits zusammengesetzt (`name` ist seit 0067
 * eine erzeugte Spalte). Die Oberflaeche baut ihn deshalb NICHT ein
 * zweites Mal aus seinen Teilen zusammen: zwei Zusammensetzungen
 * derselben Angabe laufen auseinander, sobald eine von beiden eine
 * Anrede oder ein Komma ergaenzt.
 */
const kontaktName = (k: SupplierContact): string => k.name;

/** Die Zustaendigkeiten in Worten -- in der Reihenfolge des Katalogs. */
function rollenText(k: SupplierContact, i18n: I18n): string | null {
  if (k.roles.length === 0) return null;
  return k.roles.map((r) => i18n.tCode("supRole", r)).join(", ");
}

/** Was an einem Kanal tatsaechlich dasteht -- oder nichts. */
function kanalWert(k: SupplierContact, kanal: ContactChannel, i18n: I18n): string | null {
  if (kanal === "email") return k.email;
  if (kanal === "phone") return k.phone;
  if (kanal === "mobile") return k.mobile;
  return k.messengerId === null ? null
    : `${i18n.tCode("messenger", k.messengerKind ?? "", k.messengerKind ?? "")} ${k.messengerId}`;
}

function Uebersicht({ d, i18n, lieferant, bedarf, onSpringe }: {
  /**
   * Ob diese Organisation die lieferantenspezifischen Bereiche fuehrt.
   *
   * Kommt aus dem Rollenkatalog und nicht aus einem Codevergleich hier:
   * ein Prueferdienstleister mit eigenen Bereichen soll eine Zeile in
   * `partner_roles` sein und keine Aenderung an dieser Datei.
   */
  lieferant: boolean;
  d: SupplierDetail; i18n: I18n;
  /**
   * Der offene Datenbedarf -- `null`, solange er noch gerechnet wird.
   *
   * Er wird NICHT hier gerechnet. Die Uebersicht zeigt die Zahl, die der
   * Bereich Datenbedarf ohnehin ermittelt; eine zweite Rechnung waere
   * eine zweite Wahrheit ueber dieselbe Frage.
   */
  bedarf: SupplierRequirements | null;
  /*
   * DER WEG ZUR ZUSTAENDIGEN STELLE -- nicht zu einem zweiten Editor.
   *
   * Die Uebersicht hat keine eigenen Daten: jedes Feld hier gehoert
   * einem anderen Bereich. Ein Stift an dieser Stelle waere deshalb
   * keine Bearbeitung der Uebersicht, sondern eine ZWEITE Oberflaeche
   * fuer fremde Daten -- und zwei Masken fuer dieselbe Wahrheit laufen
   * auseinander. Also zeigt sie die Luecke und fuehrt dorthin, wo man
   * sie schliesst.
   */
  onSpringe: (bereich: SupplierSectionCode, feld: string | null) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const haupt = d.contacts.find((c) => c.isPrimary) ?? null;
  /* Stillgelegte Konten zaehlen hier nicht mit: die Uebersicht sagt,
     womit man HEUTE arbeitet. */
  const aktiveKonten = d.bankAccounts.filter((k) => k.active);
  /*
   * EINE ANSCHRIFT, WIE SIE IM LAND GESCHRIEBEN WIRD.
   *
   * Zwei freie Zeilen, dann Ort mit Postleitzahl, dann Region, dann das
   * Land im Klartext. Kein „Strasse Hausnummer, PLZ Ort" -- eine
   * chinesische Fertigungsadresse nennt Gebaeude und Bezirk, und die
   * Postleitzahl steht dort hinter dem Ort oder gar nicht.
   */
  const anschrift = [
    d.street, d.addressLine2,
    [d.city, d.postalCode].filter(Boolean).join(" "),
    d.province,
    d.country === null ? null : landName(d.country, i18n.lang),
  ].filter((x): x is string => x !== null && x !== "");

  /** Eine Zeile, die zur zustaendigen Stelle fuehrt, wenn sie leer ist. */
  const luecke = (bereich: SupplierSectionCode, feld: string) => (
    <button type="button" className="sup__weg" data-testid="sup-weg"
      data-feld={feld} onClick={() => onSpringe(bereich, feld)}>
      {t("sup.goMaintain")}
    </button>
  );

  return (
    <div className="log__cluster sup__cluster">
      {/* ── PARTNER ────────────────────────────────────────────────── */}
      <Box titel={t("sup.groupPartner")}>
        <Zeile label={t("sup.shortName")}>{d.name}</Zeile>
        {/* WOFUER DIESER PARTNER STEHT -- gleich unter seinem Namen und
            nicht irgendwo weiter unten. Es ist die erste Frage. */}
        <Zeile label={t("sup.roles")}><Rollen rollen={d.roles} i18n={i18n} /></Zeile>
        <Zeile label={t("sup.legalName")}>
          {d.legalName ?? <><Unknown />{luecke("stammdaten", "legalName")}</>}
        </Zeile>
        <Zeile label={t("sup.address")}>
          {anschrift.length === 0
            ? <><Unknown />{luecke("stammdaten", "address")}</>
            : <span className="sup__anschrift">{anschrift.map((z, i) => (
                <span key={i}>{z}</span>))}</span>}
        </Zeile>
        <Zeile label={t("sup.website")}>
          {d.website === null ? <Unknown />
            : <a className="sup__link" href={d.website} target="_blank"
                 rel="noreferrer noopener">{d.website}</a>}
        </Zeile>
        {/* „Partner seit" ist ein fachliches Datum. Der Anlagezeitpunkt
            in HOMEBASE steht in den Stammdaten und nicht dafuer. */}
        <Zeile label={t("sup.partnerSince")}>
          {d.partnerSince ?? <><Unknown />{luecke("stammdaten", "partnerSince")}</>}
        </Zeile>
        <Zeile label={t("sup.status")}><StatusMarke status={d.status} i18n={i18n} /></Zeile>
      </Box>

      {/* ── HAUPTANSPRECHPARTNER ───────────────────────────────────── */}
      <Box titel={t("sup.groupContact")}>
        {/*
          * ZWEI VERSCHIEDENE LEERE ZUSTAENDE, und sie bedeuten nicht
          * dasselbe: „noch niemand erfasst" ist eine Leerstelle,
          * „erfasst, aber keiner ist der Hauptkontakt" ist eine offene
          * Entscheidung. Beides gleich zu beschriften waere eine
          * verschenkte Auskunft -- und HOMEBASE ernennt niemanden von
          * selbst.
          */}
        {haupt === null ? (
          <p className="log__hint sup__leer">
            {d.contacts.length === 0
              ? t("sup.noContacts")
              : t("sup.noPrimaryAmong", { n: String(d.contacts.length) })}
            {luecke("stammdaten", "contacts")}
          </p>
        ) : (
          <>
            <Zeile label={t("sup.contactName")}>{kontaktName(haupt)}</Zeile>
            <Zeile label={t("sup.contactJob")}>{haupt.jobTitle ?? <Unknown />}</Zeile>
            <Zeile label={t("sup.contactRoles")}>
              {rollenText(haupt, i18n) ?? <Unknown />}
            </Zeile>
            {/*
              * DER BEVORZUGTE KANAL STEHT MIT SEINEM WERT DA.
              *
              * „Bevorzugt: Messenger" allein zwaenge den Leser, darunter
              * zu suchen, welcher Messenger gemeint ist. Die Datenbank
              * garantiert seit 0067, dass der Weg gepflegt ist -- also
              * kann er auch gezeigt werden.
              */}
            <Zeile label={t("sup.preferredChannel")}>
              {haupt.preferredChannel === null ? <Unknown />
                : <>{i18n.tCode("channel", haupt.preferredChannel)}
                    {" · "}{kanalWert(haupt, haupt.preferredChannel, i18n)}</>}
            </Zeile>
            <Zeile label={t("sup.contactEmail")}>{haupt.email ?? <Unknown />}</Zeile>
            <Zeile label={t("sup.contactPhone")}>
              {haupt.phone ?? haupt.mobile ?? <Unknown />}
            </Zeile>
            {d.contacts.length > 1 && (
              <Zeile label={t("sup.contacts")}>
                {t("sup.contactsMore", { n: String(d.contacts.length - 1) })}
              </Zeile>
            )}
          </>
        )}
      </Box>

      {/* ── GESCHAEFTSBEZIEHUNG ────────────────────────────────────── */}
      {/*
        * WAS LIEFERANTENSPEZIFISCH IST, STEHT NUR BEI EINEM LIEFERANTEN.
        *
        * Sortiment, Zahlungsplan, Lieferklausel und Bewertung setzen eine
        * Lieferbeziehung voraus. Bei einem Forwarder waeren es vier
        * Luecken, die keine sind -- und jede von ihnen fuehrte per „pflegen"
        * in einen Bereich, den seine Akte gar nicht hat.
        *
        * Die Bankverbindung bleibt: eine Spedition wird auch bezahlt.
        */}
      <Box titel={t("sup.groupBusiness")}>
        {lieferant && <>
          <Zeile label={t("sup.articles")}>{d.articleCount}</Zeile>
          <Zeile label={t("sup.paymentTerms")}>
            {d.paymentTerms === null
              ? <><Unknown />{luecke("konditionen", "paymentTerms")}</>
              : <span className="ek__plan">{zahlplanSatz(d.paymentTerms, i18n)}</span>}
          </Zeile>
          <Zeile label={t("sup.incoterm")}>
            {d.incoterm ?? <><Unknown />{luecke("konditionen", "incoterm")}</>}
          </Zeile>
          <Zeile label={t("sup.currency")}>
            {d.preferredCurrency ?? <><Unknown />{luecke("konditionen", "preferredCurrency")}</>}
          </Zeile>
        </>}
        {/*
          * BANKVERBINDUNGEN NUR ALS ZAHL UND WAEHRUNG.
          *
          * KEINE Kontonummer auf der Uebersicht -- sie ist hier keine
          * Auskunft, sondern nur eine Zeichenkette, die man versehentlich
          * kopiert. Wer ueberweisen will, geht in die Finanzen; dort
          * steht sie vollstaendig und mit ihrer Bank daneben.
          */}
        <Zeile label={t("sup.bankSummary")}>
          {aktiveKonten.length === 0
            ? <><Unknown />{luecke("finanzen", "bankAccounts")}</>
            : t("sup.bankSummaryValue", {
                n: String(aktiveKonten.length),
                waehrungen: aktiveKonten
                  .map((k) => k.isPrimary
                    ? `${k.currency} (${t("sup.bankPrimary")})` : k.currency)
                  .join(" · "),
              })}
        </Zeile>
        {/* Gerechnet, nicht gespeichert -- und ohne Note steht dort
            nichts, nicht null. Eine Lieferantenbewertung ohne Lieferung
            waere allerdings gar keine Auskunft. */}
        {lieferant && (
          <Zeile label={t("sup.rating")}>
            {d.ratingOverall === null
              ? <><span className="bew__leer">{t("bew.notRated")}</span>
                  {luecke("konditionen", "rating")}</>
              : <strong>{Number(d.ratingOverall).toLocaleString(i18n.lang,
                  { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</strong>}
          </Zeile>
        )}
      </Box>

      {/* ── DATENQUALITAET ─────────────────────────────────────────── */}
      {/*
        * ZWEI VERSCHIEDENE LUECKEN IN EINEM KASTEN -- und sauber getrennt.
        *
        * Oben fehlt etwas am PARTNER, unten an seinen PRODUKTEN. Beides
        * in eine Zahl zu werfen waere bequem und falsch: die eine
        * schliesst man in den Stammdaten, die andere beim Lieferanten.
        */}
      <Box titel={t("sup.groupQuality")}>
        <Zeile label={t("sup.groupMissing")}>
          {/*
            * EINE AUFZAEHLUNG OHNE LISTENPUNKTE -- und ohne <ul>.
            *
            * Der Wert einer Zeile steht in einem <span>; eine Liste
            * darin waere ein Block im Fluss und nach den Regeln des
            * Dokuments falsch. Rechtsbuendige Aufzaehlungszeichen sahen
            * ausserdem aus wie ein Satzfehler.
            */}
          {d.missing.length === 0 ? t("sup.nothingMissing")
            : <span className="sup__fehlt">
                {d.missing.map((m) => (
                  <button key={m} type="button" className="sup__weg" data-testid="sup-weg"
                    data-feld={m} onClick={() => onSpringe("stammdaten", m)}>
                    {i18n.tCode("supMissing", m, m)}
                  </button>
                ))}
              </span>}
        </Zeile>
        {/* Offener Datenbedarf entsteht an PRODUKTEN -- die hat nur, wer
            auch liefert. */}
        {lieferant && (
        <Zeile label={t("sup.openNeed")}>
          {bedarf === null ? <span className="sup__leise">{t("common.loading")}</span>
            : bedarf.openItems === 0 ? t("sup.noOpenNeed")
            : <button type="button" className="sup__weg" data-testid="sup-weg"
                data-feld="datenbedarf" onClick={() => onSpringe("datenbedarf", null)}>
                {t("sup.openNeedValue", {
                  q: String(bedarf.questions.length),
                  a: String(bedarf.articlesWithNeed),
                })}
              </button>}
        </Zeile>
        )}
        <p className="log__hint">{t("sup.missingScope")}</p>
      </Box>
    </div>
  );
}

// ── Stammdaten und Ansprechpartner ──────────────────────────────────────────

type Feld = Record<string, string>;

const ausDaten = (d: SupplierDetail): Feld => ({
  legalName: d.legalName ?? "", supplierNumber: d.supplierNumber ?? "",
  street: d.street ?? "", addressLine2: d.addressLine2 ?? "",
  postalCode: d.postalCode ?? "", city: d.city ?? "",
  province: d.province ?? "", country: d.country ?? "", website: d.website ?? "",
  partnerSince: d.partnerSince ?? "",
});

/** Zwei Rollenlisten sind gleich, wenn sie dieselben Rollen nennen. */
function gleicheRollen(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && b.every((r) => a.includes(r));
}

function Stammdaten({ d, i18n, rollenKatalog, bearbeitet, mayWrite, vorwahl, onVerbraucht,
                     kontaktVorwahl, onKontaktBearbeiten, onKontaktVorwahlVerbraucht,
                     autoBearbeitet, onFertig, onReload, onError }: {
  /** Der Kontakt, der beim Einschalten der Bearbeitung zu oeffnen ist. */
  kontaktVorwahl: number | null;
  /** Aus dem Lesemodus heraus bearbeiten -- derselbe Modus, ein Klick. */
  onKontaktBearbeiten: (kontaktId: number) => void;
  onKontaktVorwahlVerbraucht: () => void;
  /** Wurde der Modus eigens fuer einen Kontakt eingeschaltet? */
  autoBearbeitet: boolean;
  d: SupplierDetail; i18n: I18n;
  /** Der Rollenkatalog aus dem Meta -- diese Datei fuehrt keine eigene Liste. */
  rollenKatalog: readonly PartnerRoleInfo[];
  bearbeitet: boolean; mayWrite: boolean;
  /**
   * Die Fehlstelle, aus der der Anwender hierher gesprungen ist --
   * `legalName`, `country`, `address`, `partnerSince` oder `contacts`.
   * `null`, wenn er von selbst hergekommen ist.
   */
  vorwahl: string | null;
  /** Einmal angesprungen, einmal verbraucht -- sonst springt es erneut. */
  onVerbraucht: () => void;
  /** Fertig heisst: Modus zu. */
  onFertig: () => void;
  /**
   * Neu laden, OHNE den Modus zu schliessen.
   *
   * Ein Ansprechpartner mehr ist kein Grund, die Bearbeitung zu
   * beenden -- wer drei Kontakte erfasst, will nicht dreimal den Stift
   * suchen.
   */
  onReload: () => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [entwurf, setEntwurf] = useState<Feld>(() => ausDaten(d));
  const [status, setStatus] = useState<SupplierStatus>(d.status);
  const [rollen, setRollen] = useState<readonly string[]>(d.roles);
  const [speichert, setSpeichert] = useState(false);
  /*
   * ── DER ENTWURF UEBERLEBT EINEN NEULADEVORGANG ──
   *
   * DER BEFUND AUS DER BROWSER-ABNAHME (BP-01): wer Firmierung und
   * Anschrift eingetippt hatte und dann „Ansprechpartner hinzufuegen"
   * benutzte, fand danach seine Eingaben nicht mehr vor.
   *
   * DIE URSACHE WAR NICHT DER ANSPRECHPARTNER. Das Speichern eines
   * Kontakts meldet `onChanged`, der Bereich darueber laedt den
   * Geschaeftspartner neu, und dieser Effekt setzte den Entwurf dann
   * BEDINGUNGSLOS auf den Serverstand zurueck -- mitsamt allem, was
   * daneben schon getippt war. Eine stille Verwerfung, genau die, die
   * dieser Block verbietet.
   *
   * DIE LOESUNG UEBERSCHREIBT JE FELD UND NICHT IN BAUSCH UND BOGEN:
   *
   *   unveraendertes Feld   nimmt den neuen Serverwert an -- sonst
   *                         zeigte die Maske nach dem Neuladen einen
   *                         veralteten Stand
   *   veraendertes Feld     behaelt, was der Mensch getippt hat
   *
   * Damit gibt es kein Autosave, keinen Reset und keine Ueberraschung:
   * was jemand geschrieben hat, bleibt stehen, bis er speichert oder
   * abbricht. Und weil nur die BERUEHRTEN Felder gehalten werden,
   * ueberschreibt das spaetere Speichern keine fremde Aenderung an
   * einem Feld, das dieser Mensch gar nicht angefasst hat.
   */
  /* BEIDE MERKER STEHEN VOR DEM EFFEKT, DER SIE LIEST. Eine Referenz
     unter ihrem Effekt zu deklarieren laeuft nur deshalb, weil der
     Effekt spaeter ausgefuehrt wird -- beim Lesen ist es eine Falle,
     und in diesem Projekt war es schon einmal eine. */
  const letzterStand = useRef<Feld>(ausDaten(d));
  const letzterStandNebenfelder = useRef<{ status: SupplierStatus; rollen: readonly string[] }>(
    { status: d.status, rollen: d.roles });
  useEffect(() => {
    const neu = ausDaten(d);
    const alt = letzterStand.current;
    setEntwurf((bisher) => {
      const zusammen: Feld = { ...bisher };
      for (const k of Object.keys(neu)) {
        /* Nicht angefasst? Dann gilt der Server. */
        if ((bisher[k] ?? "") === (alt[k] ?? "")) zusammen[k] = neu[k]!;
      }
      return zusammen;
    });
    setStatus((bisher) => (bisher === letzterStandNebenfelder.current.status
      ? d.status : bisher));
    setRollen((bisher) => (gleicheRollen(bisher, letzterStandNebenfelder.current.rollen)
      ? d.roles : bisher));
    letzterStand.current = neu;
    letzterStandNebenfelder.current = { status: d.status, rollen: d.roles };
  }, [d]);

  const stand = ausDaten(d);
  /* Die Reihenfolge der Rollen kommt aus dem Katalog und ist stabil --
     ein Vergleich der zusammengefuegten Liste genuegt. */
  const rollenGleich = gleicheRollen(rollen, d.roles);
  const dirty = Object.keys(stand).some((k) => (entwurf[k] ?? "") !== stand[k])
             || status !== d.status || !rollenGleich;

  async function speichern(): Promise<void> {
    setSpeichert(true);
    try {
      const leer = (v: string): string | null => (v.trim() === "" ? null : v.trim());
      await api.updateSupplier(d.id, {
        legalName: leer(entwurf["legalName"]!), supplierNumber: leer(entwurf["supplierNumber"]!),
        street: leer(entwurf["street"]!), addressLine2: leer(entwurf["addressLine2"]!),
        postalCode: leer(entwurf["postalCode"]!),
        city: leer(entwurf["city"]!), province: leer(entwurf["province"]!),
        country: leer(entwurf["country"]!), website: leer(entwurf["website"]!),
        partnerSince: leer(entwurf["partnerSince"]!),
        status,
        /* Nur mitschicken, wenn sich wirklich etwas geaendert hat -- sonst
           stuende in jedem Protokoll eine Rollenzeile ohne Rollenwechsel. */
        ...(rollenGleich ? {} : { roles: rollen }),
      });
      onFertig();
    } catch (e) { onError(e); } finally { setSpeichert(false); }
  }

  const laender = useMemo(() => laenderListe(i18n.lang), [i18n.lang]);

  /*
   * ANGEKOMMEN -- und zwar am richtigen Feld.
   *
   * Eine Fehlstelle nennt keinen Feldnamen, sondern eine fachliche
   * Luecke. „Anschrift" ist fuenf Felder; welches davon gemeint ist,
   * entscheidet sich erst hier: das ERSTE, das wirklich leer ist. Wer
   * Ort und Postleitzahl hat, aber keine Strasse, soll in der Strasse
   * landen und nicht am Anfang des Kastens.
   *
   * Gesprungen wird erst im Bearbeitungsmodus -- vorher gibt es kein
   * Eingabefeld, und ein Fokus auf einen Lesetext waere keiner. Wer das
   * Recht nicht hat, kommt trotzdem an die richtige Stelle: der Kasten
   * wird gezeigt, nur eben ohne Feld.
   */
  const rahmen = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (vorwahl === null) return;
    /*
     * ERST HANDELN, WENN ES ETWAS ZU FOKUSSIEREN GIBT.
     *
     * Der Sprung setzt Bereich und Vorwahl; die Bearbeitung geht einen
     * Durchlauf spaeter auf. Wer hier gleich beim ersten Mal handelt und
     * die Vorwahl verbraucht, springt an eine Zeile OHNE Eingabefeld --
     * und beim zweiten Durchlauf ist die Vorwahl weg. Gemessen: der
     * Reiter stimmte, der Modus stimmte, der Fokus lag nirgends.
     *
     * Ohne Schreibrecht geht die Bearbeitung gar nicht auf. Dann wird
     * die Zeile trotzdem gezeigt -- nur eben ohne Feld, und die Vorwahl
     * darf verbraucht werden, sonst wartet sie ewig.
     */
    if (!bearbeitet && mayWrite) return;
    /* Gesucht wird vom INHALTSBEREICH aus, nicht vom Feldkasten: der
       Kontaktbereich steht daneben, nicht darin. */
    const wurzel = rahmen.current?.closest(".detail__content") ?? rahmen.current;
    if (wurzel === null) return;
    const erstesLeere = (...felder: string[]): string =>
      felder.find((f) => (ausDaten(d)[f] ?? "") === "") ?? felder[0]!;
    const ziel = vorwahl === "address"
      ? erstesLeere("street", "addressLine2", "city", "postalCode", "province")
      : vorwahl === "contacts" ? "kontakte"
      : vorwahl;
    const kasten = wurzel.querySelector<HTMLElement>(`[data-ziel="${ziel}"]`)
      ?? wurzel.querySelector<HTMLElement>(`[data-ziel="${vorwahl}"]`);
    /*
     * DER KONTAKTBEREICH BEKOMMT KEINEN FELDFOKUS VON HIER -- und er
     * VERBRAUCHT die Vorwahl auch nicht hier.
     *
     * Sein Anlageformular entsteht erst einen Durchlauf spaeter. Wer die
     * Vorwahl schon jetzt verbraucht, loest ein Neuzeichnen aus -- und
     * die Aufraeumfunktion des dortigen Effekts bricht den eben
     * angeforderten Bildaufbau ab. Gemessen: Reiter und Modus stimmten,
     * der Fokus lag nirgends (SU-U6).
     *
     * Also verbraucht der Bereich sie, DER DEN FOKUS SETZT. Nur wenn das
     * Formular gar nicht aufgeht -- ohne Schreibrecht --, wird sie hier
     * verbraucht: sonst wartet sie ewig.
     */
    const eigenerFokus = ziel === "kontakte" && bearbeitet && mayWrite;
    return zeigeUndFokussiere(
      kasten,
      ziel === "kontakte" ? null
        : kasten?.querySelector<HTMLElement>("input, select, textarea") ?? null,
      eigenerFokus ? undefined : onVerbraucht);
  }, [vorwahl, bearbeitet, mayWrite, d, onVerbraucht]);

  /* `data-ziel` ist die Kennung, ueber die ein Sprung sein Feld findet --
     dieselbe Zeichenkette wie in der Fehlliste. */
  const feld = (k: string, label: string, extra?: { typ?: string; hinweis?: string }) => (
    <Zeile label={label} ziel={k}>
      {bearbeitet
        ? <input className="log__input log__input--text" value={entwurf[k] ?? ""}
            type={extra?.typ ?? "text"} disabled={speichert} aria-label={label}
            placeholder={extra?.hinweis}
            onChange={(e) => setEntwurf({ ...entwurf, [k]: e.target.value })} />
        : (stand[k] === "" ? <Unknown /> : stand[k])}
    </Zeile>
  );

  return (
    <>
      {bearbeitet && (
        <div className="log__modus" role="status">
          <span className="log__moduslabel">{t("sup.editing")}</span>
          <span className="log__modushint">{t("sup.editHint")}</span>
          <span className="log__modusactions">
            {dirty && (
              <button className="btn btn--primary btn--small log__save" disabled={speichert}
                onClick={() => void speichern()}>
                {speichert ? t("props.saving") : t("log.save")}
              </button>
            )}
            <button className="btn btn--small log__cancel" disabled={speichert}
              onClick={() => { setEntwurf(ausDaten(d)); setStatus(d.status); onFertig(); }}>
              {dirty ? t("common.cancel") : t("tk.done")}
            </button>
          </span>
        </div>
      )}

      <div className="log__cluster sup__cluster" ref={rahmen}>
        <Box titel={t("sup.groupCompany")} klasse="sup__gross">
          {/*
            * DER KURZNAME WIRD HIER NICHT GEAENDERT.
            *
            * Er steht in VERSALIEN und haengt an der
            * Zusammenfuehrungsgeschichte: unter ihm wurden Datensaetze
            * vereinigt und Grabsteine gesetzt. Ihn beilaeufig
            * umzubenennen waere ein Eingriff in die Identitaet des
            * Partners und kein Pflegen von Stammdaten.
            */}
          <Zeile label={t("sup.shortName")}>{d.name}</Zeile>
          {/*
            * DIE ROLLEN SIND EINE ENTSCHEIDUNG UEBER DIE ZUSAMMENARBEIT
            * -- und werden deshalb hier gepflegt, nicht anderswo.
            *
            * Firma X kann Lieferant UND Forwarder sein. Zwei Karteien
            * dafuer waeren zwei Anschriften und zwei Gelegenheiten, eine
            * davon zu vergessen.
            */}
          <Zeile label={t("sup.roles")} ziel="roles">
            {bearbeitet
              ? <span className="sup__krollen" role="group" aria-label={t("sup.roles")}>
                  {rollenKatalog.map((r) => (
                    <label key={r.code} className="sup__schalter">
                      <input type="checkbox" data-testid="sup-rolle" data-rolle={r.code}
                        checked={rollen.includes(r.code)} disabled={speichert}
                        onChange={(e) => setRollen(e.target.checked
                          ? [...rollen, r.code]
                          : rollen.filter((x) => x !== r.code))} />
                      {i18n.tCode("partnerRole", r.code, r.code)}
                    </label>
                  ))}
                </span>
              : <Rollen rollen={d.roles} i18n={i18n} />}
          </Zeile>
          {feld("legalName", t("sup.legalName"))}
          {feld("supplierNumber", t("sup.supplierNumber"), { hinweis: t("sup.optional") })}
          {feld("website", t("sup.website"))}
          {/*
            * VIER GESCHAEFTSSTATUS STATT EINES SCHALTERS (0042). Ein
            * Backup-Partner ist nicht „inaktiv", und wer im Sourcing
            * steckt, ist nicht „aktiv" -- beides war mit ja/nein nicht
            * sagbar.
            */}
          <Zeile label={t("sup.status")} ziel="status">
            {bearbeitet
              ? <select className="log__input" value={status} disabled={speichert}
                  aria-label={t("sup.status")}
                  onChange={(e) => setStatus(e.target.value as SupplierStatus)}>
                  {SUPPLIER_STATUSES.map((x) => (
                    <option key={x} value={x}>{i18n.tCode("supStatus", x)}</option>
                  ))}
                </select>
              : <StatusMarke status={d.status} i18n={i18n} />}
          </Zeile>
          {/* „Partner seit" ist ein Datum, kein Freitext -- und es ist
              NICHT der Anlagezeitpunkt. Der steht darunter. */}
          {feld("partnerSince", t("sup.partnerSince"), { typ: "date" })}
          <Zeile label={t("sup.createdAt")}>{d.createdAt.slice(0, 10)}</Zeile>
          {/*
            * DAS EINZELNE NOTIZFELD IST FORT -- und das ist der Punkt.
            *
            * Die Implementierung 002 hatte es sichtbar gemacht; seit es
            * die Notizhistorie gibt (0068), beantwortete es dieselbe
            * Frage ein zweites Mal. Zwei Felder mit demselben Zweck
            * laufen ab dem ersten Eintrag auseinander. Was dastand, ist
            * als erste Notiz uebernommen worden.
            */}
        </Box>

        {/*
          * EINE ANSCHRIFT OHNE EUROPAEISCHES ZWANGSSCHEMA.
          *
          * Zwei freie Zeilen statt „Strasse + Hausnummer": eine
          * chinesische Fertigungsadresse nennt Gebaeude, Halle und
          * Bezirk, bevor sie zur Stadt kommt. Die Postleitzahl ist
          * optional -- es gibt Laender ohne. Und das Land ist eine
          * AUSWAHL, kein Textfeld: gespeichert wird der ISO-Code, den
          * die Datenbank seit 0005 verlangt, angezeigt der Name in der
          * Sprache des Benutzers. Genau hier stand der Fehler „Interner
          * Fehler": die Maske schickte „China" in eine Schranke, die
          * „CN" erwartet.
          */}
        <Box titel={t("sup.groupAddress")} klasse="sup__gross">
          {feld("street", t("sup.addressLine1"))}
          {feld("addressLine2", t("sup.addressLine2"), { hinweis: t("sup.addressLine2Hint") })}
          {feld("city", t("sup.city"))}
          {feld("postalCode", t("sup.postalCode"), { hinweis: t("sup.optional") })}
          {feld("province", t("sup.province"), { hinweis: t("sup.optional") })}
          <Zeile label={t("sup.country")} ziel="country">
            {bearbeitet
              ? <select className="log__input sup__land" value={entwurf["country"] ?? ""}
                  disabled={speichert} aria-label={t("sup.country")}
                  onChange={(e) => setEntwurf({ ...entwurf, country: e.target.value })}>
                  <option value="">{t("sup.countryNone")}</option>
                  {laender.map((l) => (
                    <option key={l.code} value={l.code}>{l.name}</option>
                  ))}
                </select>
              : (d.country === null ? <Unknown /> : landName(d.country, i18n.lang))}
          </Zeile>
        </Box>
      </div>

      <Kontakte supplierId={d.id} kontakte={d.contacts} i18n={i18n}
        anlageOeffnen={vorwahl === "contacts"} onVerbraucht={onVerbraucht}
        mayWrite={mayWrite && bearbeitet}
        /* Darf der Anwender ueberhaupt schreiben? Dann ist ein
           Ansprechpartner auch im Lesemodus anklickbar -- er schaltet
           die Bearbeitung ein und oeffnet sich. */
        darfBearbeiten={mayWrite}
        kontaktVorwahl={kontaktVorwahl}
        onKontaktBearbeiten={onKontaktBearbeiten}
        onKontaktVorwahlVerbraucht={onKontaktVorwahlVerbraucht}
        /*
         * ZWEI STUFEN VERLASSEN, OHNE ETWAS ZU VERLIEREN.
         *
         * Der Befund: nach „Abbrechen" in der Kontaktmaske blieb der
         * Bearbeitungsmodus des Geschaeftspartners an, und man musste
         * zusaetzlich „Fertig" druecken -- als stuende noch etwas aus.
         *
         * Hier wird er nur dann geschlossen, wenn BEIDES gilt: der
         * Modus wurde eigens fuer diesen Kontakt eingeschaltet, UND am
         * Partner selbst steht nichts Ungespeichertes. `dirty` ist
         * genau diese Frage -- der Schutz gegen unbeabsichtigten
         * Verlust bleibt also vollstaendig.
         */
        onKontaktAbbruch={() => { if (autoBearbeitet && !dirty) onFertig(); }}
        onChanged={onReload} onError={onError} />

      {/*
        * BANKDATEN STEHEN HIER NICHT -- und zwar mit Absicht.
        *
        * Sie sind die sicherheitskritischste Angabe eines Partners:
        * geaenderte Kontodaten sind der haeufigste reale Zahlungsschaden.
        * Sie brauchen ein eigenes Recht, ein eigenes Protokoll und eine
        * Bestaetigung durch einen zweiten Menschen. Ein Bankfeld
        * „nebenbei" in der Stammdatenmaske waere genau der Fehler, den
        * man hinterher nicht mehr einfaengt.
        */}
    </>
  );
}

/**
 * Der Entwurf eines Ansprechpartners.
 *
 * ALLE FELDER ALS ZEICHENKETTE, auch die leeren. Ein Formular, in dem
 * ein Feld mal `null` und mal `""` ist, zwingt jede Eingabe zu einer
 * Fallunterscheidung -- und beim Messenger war genau dieser Unterschied
 * die Grenze zwischen „kein Kanal" und „halber Kanal". Uebersetzt wird
 * erst beim Absenden, an einer Stelle.
 */
interface KEntwurf {
  firstName: string; lastName: string; jobTitle: string; department: string;
  roles: readonly SupplierContactRole[];
  email: string; phone: string; mobile: string;
  messengerKind: MessengerKind | "";
  messengerId: string;
  preferredChannel: ContactChannel | "";
  isPrimary: boolean; active: boolean; note: string;
}

const LEERER_KONTAKT: KEntwurf = {
  firstName: "", lastName: "", jobTitle: "", department: "", roles: [],
  email: "", phone: "", mobile: "", messengerKind: "", messengerId: "",
  preferredChannel: "", isPrimary: false, active: true, note: "",
};

const ausKontakt = (k: SupplierContact): KEntwurf => ({
  firstName: k.firstName ?? "", lastName: k.lastName,
  jobTitle: k.jobTitle ?? "", department: k.department ?? "",
  roles: k.roles,
  email: k.email ?? "", phone: k.phone ?? "", mobile: k.mobile ?? "",
  messengerKind: k.messengerKind ?? "", messengerId: k.messengerId ?? "",
  preferredChannel: k.preferredChannel ?? "",
  isPrimary: k.isPrimary, active: k.active, note: k.note ?? "",
});

/** Eine leere Eingabe ist keine Angabe -- „" wird zu `null`, nicht zu „". */
const leer = (v: string): string | null => (v.trim() === "" ? null : v.trim());

const alsAnfrage = (e: KEntwurf): UpsertSupplierContactRequest => ({
  firstName: leer(e.firstName), lastName: e.lastName.trim(),
  jobTitle: leer(e.jobTitle), department: leer(e.department),
  roles: e.roles,
  email: leer(e.email), phone: leer(e.phone), mobile: leer(e.mobile),
  messengerKind: e.messengerKind === "" ? null : e.messengerKind,
  messengerId: e.messengerKind === "" ? null : leer(e.messengerId),
  preferredChannel: e.preferredChannel === "" ? null : e.preferredChannel,
  isPrimary: e.isPrimary, active: e.active, note: leer(e.note),
});

/** Welche Kanaele in DIESEM Entwurf tatsaechlich gepflegt sind. */
const gepflegteKanaele = (e: KEntwurf): ContactChannel[] =>
  CONTACT_CHANNELS.filter((k) =>
    k === "email" ? e.email.trim() !== ""
    : k === "phone" ? e.phone.trim() !== ""
    : k === "mobile" ? e.mobile.trim() !== ""
    : e.messengerKind !== "" && e.messengerId.trim() !== "");

/**
 * DIE MASKE EINES ANSPRECHPARTNERS -- eine, fuer neu und fuer bestehend.
 *
 * Bis V1.2 gab es nur ein ANLAGEformular; ein bestehender Kontakt liess
 * sich nicht aendern. Wer eine Mailadresse korrigieren wollte, musste
 * loeschen und neu anlegen -- und verlor dabei die Kennung, die
 * Geschichte und den Hauptkontakt-Status. Genau das ist der Grund, warum
 * hier EIN Formular steht und nicht ein zweites daneben: zwei Masken fuer
 * dieselben dreizehn Felder laufen auseinander, und die zweite ist die,
 * die beim naechsten Feld vergessen wird.
 */
function KontaktMaske({ entwurf, setEntwurf, speichert, i18n, nameFeld,
                        onSpeichern, onAbbruch, neu }: {
  entwurf: KEntwurf; setEntwurf: (e: KEntwurf) => void;
  speichert: boolean; i18n: I18n;
  nameFeld?: React.RefObject<HTMLInputElement | null>;
  onSpeichern: () => void; onAbbruch: () => void;
  /** Nur fuer die Beschriftung der Schaltflaeche -- fachlich derselbe Weg. */
  neu: boolean;
}): React.JSX.Element {
  const t = i18n.t;
  const kanaele = gepflegteKanaele(entwurf);
  /*
   * DER BEVORZUGTE KANAL KANN NUR AUF ETWAS ZEIGEN, DAS DASTEHT.
   *
   * Wer die Mobilnummer wieder loescht, verliert die Bevorzugung
   * mit -- sonst schickte die Maske eine Angabe, die die Datenbank seit
   * 0067 zu Recht abweist, und der Benutzer bekaeme eine Absage fuer
   * etwas, das er gar nicht angefasst hat.
   */
  const aendere = (teil: Partial<KEntwurf>): void => {
    const naechster = { ...entwurf, ...teil };
    if (naechster.preferredChannel !== ""
        && !gepflegteKanaele(naechster).includes(naechster.preferredChannel)) {
      naechster.preferredChannel = "";
    }
    setEntwurf(naechster);
  };
  const feld = (
    k: "firstName" | "lastName" | "jobTitle" | "department" | "email" | "phone"
     | "mobile" | "messengerId" | "note",
    label: string, klasse?: string,
  ) => (
    <label className={klasse ? `sup__kfeld ${klasse}` : "sup__kfeld"}>
      <span className="sup__klabel">{label}</span>
      {/* `data-feld` ist die Kennung, ueber die eine Pruefung dieses Feld
          findet -- dieselbe Zeichenkette wie im Entwurf. Ueber die
          Reihenfolge zu suchen waere beim naechsten Feld falsch. */}
      <input className="log__input log__input--text" value={entwurf[k]} data-feld={k}
        aria-label={label} placeholder={label} disabled={speichert}
        ref={k === "lastName" ? nameFeld : undefined}
        onChange={(e) => aendere({ [k]: e.target.value } as Partial<KEntwurf>)} />
    </label>
  );

  return (
    <div className="sup__kform" data-testid="sup-kontaktmaske">
      {/* Der Nachname ist die verlaessliche Haelfte -- eine Anfrage in
          China beginnt damit. Deshalb steht er zuerst und ist das
          einzige Pflichtfeld. */}
      {feld("lastName", t("sup.lastName"))}
      {feld("firstName", t("sup.firstName"))}
      {feld("jobTitle", t("sup.contactJob"))}
      {feld("department", t("sup.contactDepartment"))}

      {/*
        * MEHRERE ZUSTAENDIGKEITEN -- Kaestchen, keine Auswahlliste.
        *
        * Wer bei einem kleinen Fertiger Sales macht, macht haeufig auch
        * Logistics. Eine Mehrfachauswahl in einem <select> verbirgt,
        * dass mehr als eines geht; sieben Kaestchen zeigen es.
        */}
      <fieldset className="sup__krollen" disabled={speichert}>
        <legend className="sup__klabel">{t("sup.contactRoles")}</legend>
        {SUPPLIER_CONTACT_ROLES.map((r) => (
          <label key={r} className="sup__schalter">
            <input type="checkbox" checked={entwurf.roles.includes(r)}
              data-rolle={r}
              onChange={(e) => aendere({
                roles: e.target.checked
                  ? SUPPLIER_CONTACT_ROLES.filter((x) => x === r || entwurf.roles.includes(x))
                  : entwurf.roles.filter((x) => x !== r),
              })} />
            {i18n.tCode("supRole", r)}
          </label>
        ))}
      </fieldset>

      {feld("email", t("sup.contactEmail"))}
      {feld("phone", t("sup.contactPhone"))}
      {feld("mobile", t("sup.contactMobile"))}

      <label className="sup__kfeld">
        <span className="sup__klabel">{t("sup.messengerKind")}</span>
        {/*
          * DAS KENNUNGSFELD ERSCHEINT ERST MIT DER ART.
          *
          * Bis V1.1 stand es immer da. Wer eine WeChat-Kennung eintippte,
          * ohne die Art zu waehlen, schickte einen halben Kanal -- der
          * Server wies ihn zu Recht ab, und die Oberflaeche meldete
          * „unbekannter Fehler". Wird die Art zurueckgenommen, geht die
          * Kennung mit; ein Rest im unsichtbaren Feld waere derselbe
          * halbe Kanal.
          */}
        <select className="log__input" value={entwurf.messengerKind} disabled={speichert}
          aria-label={t("sup.messengerKind")}
          onChange={(e) => aendere({
            messengerKind: e.target.value as MessengerKind | "",
            messengerId: e.target.value === "" ? "" : entwurf.messengerId,
          })}>
          <option value="">{t("sup.messengerNone")}</option>
          {MESSENGER_KINDS.map((m) => (
            <option key={m} value={m}>{i18n.tCode("messenger", m)}</option>
          ))}
        </select>
      </label>
      {entwurf.messengerKind !== "" && feld("messengerId", t("sup.messengerId"))}

      {/*
        * NUR GEPFLEGTE WEGE STEHEN ZUR WAHL. Ein „Mobil", das man
        * auswaehlen kann, ohne eine Mobilnummer zu haben, waere ein
        * Versprechen, das beim ersten Anruf platzt -- und die Datenbank
        * wiese es ohnehin ab.
        */}
      <label className="sup__kfeld">
        <span className="sup__klabel">{t("sup.preferredChannel")}</span>
        <select className="log__input" value={entwurf.preferredChannel}
          disabled={speichert || kanaele.length === 0}
          aria-label={t("sup.preferredChannel")} data-testid="sup-bevorzugt"
          onChange={(e) => aendere({ preferredChannel: e.target.value as ContactChannel | "" })}>
          <option value="">{t("sup.preferredNone")}</option>
          {kanaele.map((k) => (
            <option key={k} value={k}>{i18n.tCode("channel", k)}</option>
          ))}
        </select>
      </label>

      {feld("note", t("sup.contactNote"), "sup__kfeld--breit")}

      <label className="sup__schalter">
        <input type="checkbox" checked={entwurf.isPrimary} disabled={speichert}
          data-testid="sup-haupt"
          onChange={(e) => aendere({ isPrimary: e.target.checked })} />
        {t("sup.primary")}
      </label>
      {/* Ein ausgeschiedener Ansprechpartner wird nicht geloescht: was er
          entschieden hat, bleibt Teil der Geschichte. Er wird still. */}
      <label className="sup__schalter">
        <input type="checkbox" checked={entwurf.active} disabled={speichert}
          data-testid="sup-aktiv"
          onChange={(e) => aendere({ active: e.target.checked })} />
        {t("sup.contactActive")}
      </label>

      <span className="sup__kformaktionen">
        <button className="btn btn--primary btn--small" data-testid="sup-kontaktspeichern"
          disabled={speichert || entwurf.lastName.trim() === ""}
          onClick={onSpeichern}>
          {speichert ? t("props.saving") : neu ? t("sup.saveContact") : t("log.save")}
        </button>
        <button className="btn btn--small" disabled={speichert}
          onClick={onAbbruch}>{t("common.cancel")}</button>
      </span>
    </div>
  );
}

/**
 * DIE ANSPRECHPARTNER ALS ECHTE ENTITAETSLISTE.
 *
 * Jede Zeile ist ein Mensch mit einer Kennung, und jede Zeile traegt die
 * Aktionen, die zu IHM gehoeren: Stift zum Aendern, Auszeichnung zum
 * Hauptkontakt, Entfernen als bewusster Schritt. Das Plus legt einen
 * neuen an. Dieselbe Regel wie in der Produktakte -- Stift aendert,
 * Plus ergaenzt, Papierkorb entfernt.
 */
function Kontakte({ supplierId, kontakte, i18n, anlageOeffnen, onVerbraucht, mayWrite,
                    darfBearbeiten, kontaktVorwahl, onKontaktBearbeiten,
                    onKontaktVorwahlVerbraucht, onKontaktAbbruch,
                    onChanged, onError }: {
  supplierId: number; kontakte: readonly SupplierContact[]; i18n: I18n;
  /**
   * Ob der Anwender ueberhaupt schreiben darf -- unabhaengig davon, ob
   * der Bearbeitungsmodus gerade an ist. Nur davon haengt ab, ob eine
   * Kontaktzeile im Lesemodus anklickbar ist.
   */
  darfBearbeiten: boolean;
  /** Dieser Kontakt soll sich oeffnen, sobald der Modus eingeschaltet ist. */
  kontaktVorwahl: number | null;
  onKontaktBearbeiten: (kontaktId: number) => void;
  onKontaktVorwahlVerbraucht: () => void;
  /**
   * Eine Kontaktbearbeitung wurde ABGEBROCHEN -- nichts geschrieben.
   *
   * Der Bereich darueber entscheidet, was daraus folgt: wurde der
   * Bearbeitungsmodus nur FUER DIESEN Kontakt eingeschaltet und steht am
   * Geschaeftspartner selbst nichts Offenes, braucht der Anwender
   * danach nichts mehr zu bestaetigen.
   */
  onKontaktAbbruch: () => void;
  /**
   * Der Anwender kam aus der Uebersicht, weil ein Ansprechpartner fehlt
   * -- dann steht das Anlageformular gleich offen. Es wird dadurch KEIN
   * Kontakt angelegt: das Formular ist leer, und gespeichert wird erst
   * auf Klick.
   */
  anlageOeffnen: boolean;
  /**
   * Die Vorwahl zurueckgeben -- NACH dem Fokus, nicht davor.
   *
   * Dieser Bereich setzt den Fokus in das Anlageformular; wer die
   * Vorwahl vorher verbraucht, bricht genau diesen Bildaufbau ab. Der
   * Bereich, der den Fokus setzt, beendet deshalb auch den Sprung.
   */
  onVerbraucht: () => void;
  mayWrite: boolean; onChanged: () => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  /*
   * WAS GERADE BEARBEITET WIRD -- eine Kennung oder „neu", nie beides.
   *
   * Zwei offene Masken nebeneinander waeren zwei Entwuerfe desselben
   * Bereichs; wer dann speichert, weiss nicht, welcher gemeint war.
   */
  const [offen, setOffen] = useState<number | "neu" | null>(null);
  const [entwurf, setEntwurf] = useState<KEntwurf>(LEERER_KONTAKT);
  const [speichert, setSpeichert] = useState(false);
  /* Ein Entfernen ist endgueltig. Es bekommt deshalb einen zweiten
     Schritt -- und der steht an der Zeile, nicht in einem Fenster. */
  const [entfernt, setEntfernt] = useState<number | null>(null);

  /*
   * DER BEREICH, DEM DAS FORMULAR GEHOERT, SETZT DEN FOKUS SELBST.
   *
   * Kommt der Anwender aus der Uebersicht, weil ein Ansprechpartner
   * fehlt, steht das Anlageformular offen -- und der Cursor im
   * Nachnamen. Es entsteht dadurch KEIN Kontakt: das Formular ist leer,
   * und gespeichert wird erst auf Klick.
   */
  const nameFeld = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (anlageOeffnen && mayWrite) {
      setOffen((x) => (x === null ? "neu" : x));
      setEntwurf((x) => (x === LEERER_KONTAKT ? LEERER_KONTAKT : x));
    }
  }, [anlageOeffnen, mayWrite]);
  useEffect(() => {
    if (!anlageOeffnen || offen !== "neu") return;
    return zeigeUndFokussiere(nameFeld.current, nameFeld.current, onVerbraucht);
  }, [anlageOeffnen, offen, onVerbraucht]);
  /* Wer das Schreibrecht verliert oder den Bereich schliesst, verliert
     auch den Entwurf -- ein Formular ohne Weg zum Speichern ist eine
     Einladung, Arbeit zu verlieren. */
  useEffect(() => { if (!mayWrite) { setOffen(null); setEntfernt(null); } }, [mayWrite]);

  function oeffneNeu(): void {
    setEntwurf(LEERER_KONTAKT); setOffen("neu"); setEntfernt(null);
  }
  /*
   * DIE VORWAHL EINLOESEN -- und nur einmal.
   *
   * Der Anwender hat im Lesemodus auf eine Kontaktzeile geklickt. Der
   * Elternbereich hat daraufhin den Bearbeitungsmodus eingeschaltet und
   * die Kennung hierher gereicht; jetzt oeffnet sich genau dieser
   * Kontakt. Danach wird die Vorwahl zurueckgegeben, sonst spraenge sie
   * beim naechsten Rendern erneut auf.
   */
  useEffect(() => {
    if (!mayWrite || kontaktVorwahl === null) return;
    const k = kontakte.find((x) => x.id === kontaktVorwahl);
    if (k !== undefined) { setEntwurf(ausKontakt(k)); setOffen(k.id); setEntfernt(null); }
    onKontaktVorwahlVerbraucht();
  }, [mayWrite, kontaktVorwahl, kontakte, onKontaktVorwahlVerbraucht]);

  function oeffneBestehend(k: SupplierContact): void {
    /* VORBEFUELLT MIT DEM, WAS DASTEHT -- und nicht mit einer Auswahl
       davon. Ein Feld, das die Maske nicht zeigt, wuerde beim Speichern
       geleert, weil der Aufruf die ganze Entitaet ersetzt. */
    setEntwurf(ausKontakt(k)); setOffen(k.id); setEntfernt(null);
  }

  async function speichern(): Promise<void> {
    if (offen === null) return;
    setSpeichert(true);
    try {
      /*
       * DIESELBE KENNUNG BLEIBT. Aendern heisst aendern -- nicht
       * loeschen und neu anlegen. Der Weg dorthin ist die bestehende
       * PATCH-Adresse; eine zweite waere eine zweite Schreibstelle.
       */
      if (offen === "neu") await api.createSupplierContact(supplierId, alsAnfrage(entwurf));
      else await api.updateSupplierContact(supplierId, offen, alsAnfrage(entwurf));
      setOffen(null); setEntwurf(LEERER_KONTAKT); onChanged();
    } catch (e) { onError(e); } finally { setSpeichert(false); }
  }
  async function entfernen(id: number): Promise<void> {
    setSpeichert(true);
    try { await api.deleteSupplierContact(supplierId, id); setEntfernt(null); onChanged(); }
    catch (e) { onError(e); } finally { setSpeichert(false); }
  }
  /*
   * ZUM HAUPTANSPRECHPARTNER -- der ganze Kontakt, unveraendert bis auf
   * diese eine Auszeichnung. Der Aufruf ersetzt die Entitaet; ihm nur
   * `isPrimary` mitzugeben hiesse, alles andere zu leeren.
   */
  async function zumHaupt(k: SupplierContact): Promise<void> {
    setSpeichert(true);
    try {
      await api.updateSupplierContact(supplierId, k.id,
        alsAnfrage({ ...ausKontakt(k), isPrimary: true }));
      onChanged();
    } catch (e) { onError(e); } finally { setSpeichert(false); }
  }

  return (
    <section className="log__group sup__kontakte" data-ziel="kontakte">
      <div className="sup__kkopf">
        <h4 className="log__grouptitle">{t("sup.groupContacts")}</h4>
        {/* PLUS ERGAENZT. Es steht am Kopf der Liste, wie ueberall. */}
        {mayWrite && offen === null && (
          <button className="btn btn--small sup__plus" data-testid="sup-kontaktneu"
            onClick={oeffneNeu}>{t("sup.addContact")}</button>
        )}
      </div>
      {/*
        * MEHRERE ANSPRECHPARTNER SIND DER NORMALFALL -- Sales, QC und
        * Finance sind selten dieselbe Person. Genau ein Kontakt ist der
        * Hauptansprechpartner; die Datenbank laesst keinen zweiten zu.
        */}
      {kontakte.length === 0 ? (
        <p className="log__hint">{t("sup.noContacts")}</p>
      ) : (
        <ul className="sup__kontaktliste">
          {kontakte.map((k) => (
            <li key={k.id} data-kontakt={k.id}
                className={[
                  "sup__kontakt",
                  k.isPrimary ? "sup__kontakt--haupt" : "",
                  k.active ? "" : "sup__kontakt--still",
                ].filter(Boolean).join(" ")}>
              {offen === k.id ? (
                <KontaktMaske entwurf={entwurf} setEntwurf={setEntwurf} neu={false}
                  speichert={speichert} i18n={i18n}
                  onSpeichern={() => void speichern()}
                  onAbbruch={() => {
                    setOffen(null); setEntwurf(LEERER_KONTAKT); onKontaktAbbruch();
                  }} />
              ) : (
                <>
                  <span className="sup__kzeile">
                    {/*
                      * IM LESEMODUS IST DER NAME DER EINSTIEG.
                      *
                      * Vorher war ein Ansprechpartner dort gar nicht
                      * anklickbar -- erst der Stift der Stammdaten,
                      * dann der Stift der Zeile, dann die Maske. Drei
                      * Schritte fuer eine Telefonnummer.
                      *
                      * Es entsteht dadurch KEIN zweiter
                      * Bearbeitungsweg: der Klick schaltet denselben
                      * Modus ein und waehlt denselben Kontakt vor, den
                      * der Stift danach ebenfalls oeffnen wuerde.
                      */}
                    {!mayWrite && darfBearbeiten ? (
                      <button type="button" className="sup__kname sup__kname--ziel"
                        data-testid="sup-kontaktoeffnen" data-kontakt={k.id}
                        title={t("props.edit")}
                        aria-label={t("sup.editContactAria", { name: kontaktName(k) })}
                        onClick={() => onKontaktBearbeiten(k.id)}>
                        {kontaktName(k)}<span aria-hidden="true" className="sup__knamestift">{PENCIL}</span>
                      </button>
                    ) : (
                      <span className="sup__kname">{kontaktName(k)}</span>
                    )}
                    {k.isPrimary && <span className="sup__tag">{t("sup.primary")}</span>}
                    {!k.active && (
                      <span className="sup__tag sup__tag--aus">{t("sup.contactInactive")}</span>
                    )}
                    {k.jobTitle !== null && <span className="sup__kfunktion">{k.jobTitle}</span>}
                    {rollenText(k, i18n) !== null && (
                      <span className="sup__krolle">{rollenText(k, i18n)}</span>
                    )}
                  </span>
                  <span className="sup__kkontakt">
                    {CONTACT_CHANNELS.map((kanal) => {
                      const wert = kanalWert(k, kanal, i18n);
                      if (wert === null) return null;
                      /* Der bevorzugte Weg ist ausgezeichnet, nicht
                         wiederholt -- er steht ohnehin schon da. */
                      return (
                        <span key={kanal}
                          className={k.preferredChannel === kanal
                            ? "sup__kweg sup__kweg--bevorzugt" : "sup__kweg"}
                          title={k.preferredChannel === kanal
                            ? t("sup.preferredChannel") : undefined}>
                          {k.preferredChannel === kanal && (
                            <span aria-hidden="true" className="sup__kstern">★</span>
                          )}
                          {wert}
                        </span>
                      );
                    })}
                  </span>
                  {mayWrite && (
                    <span className="sup__kaktionen">
                      {/* STIFT AENDERT -- dieselbe Kennung, dieselbe
                          Geschichte. */}
                      <button type="button" className="subnav__pencil"
                        data-testid="sup-kontaktstift" data-kontakt={k.id}
                        title={t("props.edit")}
                        aria-label={t("sup.editContactAria", { name: kontaktName(k) })}
                        disabled={speichert || offen !== null}
                        onClick={() => oeffneBestehend(k)}>{PENCIL}</button>
                      {!k.isPrimary && (
                        <button type="button" className="bew__weg"
                          disabled={speichert || offen !== null}
                          onClick={() => void zumHaupt(k)}>{t("sup.makePrimary")}</button>
                      )}
                      {entfernt === k.id ? (
                        <>
                          <span className="sup__kfrage">{t("sup.removeSure")}</span>
                          <button type="button" className="bew__weg" disabled={speichert}
                            data-testid="sup-entfernen-ja"
                            onClick={() => void entfernen(k.id)}>{t("sup.removeYes")}</button>
                          <button type="button" className="bew__weg" disabled={speichert}
                            onClick={() => setEntfernt(null)}>{t("common.cancel")}</button>
                        </>
                      ) : (
                        <button type="button" className="bew__weg"
                          data-testid="sup-entfernen"
                          disabled={speichert || offen !== null}
                          onClick={() => setEntfernt(k.id)}>{t("sup.removeContact")}</button>
                      )}
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {mayWrite && offen === "neu" && (
        <KontaktMaske entwurf={entwurf} setEntwurf={setEntwurf} neu={true}
          speichert={speichert} i18n={i18n} nameFeld={nameFeld}
          onSpeichern={() => void speichern()}
          onAbbruch={() => { setOffen(null); setEntwurf(LEERER_KONTAKT); }} />
      )}
    </section>
  );
}

// ── Produkte ────────────────────────────────────────────────────────────────

/**
 * Die Artikel dieses Lieferanten -- sortierbar und anklickbar.
 *
 * DIE EINZIGE QUELLE IST `supplier_articles`. Diese Ansicht ist die
 * umgedrehte Blickrichtung derselben Beziehung, die der Artikel im
 * Einkauf zeigt -- keine Kopie und keine zweite Zuordnung. Hat ein
 * Artikel weitere Lieferanten, steht es dabei.
 */
type Feldname = "articleNumber" | "shortDescription" | "supplierArticleNumber"
              | "moq" | "leadTimeDays";

/* ── Datenbedarf ────────────────────────────────────────────────────────── */

/**
 * WAS AN DEN PRODUKTEN DIESES LIEFERANTEN FEHLT -- nach FRAGE geordnet.
 *
 * DIE ENTSCHEIDENDE DARSTELLUNGSFRAGE
 *
 * Bei AGC sind es 778 fehlende Einzelangaben. Eine Liste mit 778 Zeilen
 * ist kein Arbeitsvorrat, sondern eine Wand -- man liest sie einmal und
 * danach nie wieder. Dahinter stehen aber nur DREIZEHN verschiedene
 * Fragen. Genau die zeigt dieser Bereich: dreizehn Zeilen, jede mit der
 * Anzahl betroffener Artikel, und die Artikel erst auf Klick.
 *
 * WAS HIER NICHT PASSIERT
 *
 * Es wird nichts entschieden und nichts gespeichert. „Beim Lieferanten
 * beschaffbar" heisst NICHT „zur Beschaffung freigegeben" -- diese
 * Entscheidung gibt es in HOMEBASE noch gar nicht. Die Liste wird bei
 * jedem Oeffnen neu gerechnet; wer einen Wert pflegt, sieht sie beim
 * naechsten Mal kuerzer.
 */
/* ── Datenbedarf ────────────────────────────────────────────────────────── */

/**
 * WAS AN DEN PRODUKTEN DIESES LIEFERANTEN FEHLT -- zwei Sichten auf
 * DIESELBE Rechnung.
 *
 * DIE ENTSCHEIDENDE DARSTELLUNGSFRAGE
 *
 * Bei AGC sind es 778 fehlende Einzelangaben. Eine Liste mit 778 Zeilen
 * ist kein Arbeitsvorrat, sondern eine Wand -- man liest sie einmal und
 * danach nie wieder. Dahinter stehen aber nur dreizehn verschiedene
 * Fragen und neunzig betroffene Produkte. Beide Zahlen sind lesbar, und
 * beide beantworten eine andere Arbeitsfrage:
 *
 *   NACH FRAGE    „Ich schreibe dem Lieferanten wegen des
 *                  Gehaeusematerials -- welche Produkte betrifft das?"
 *   NACH PRODUKT  „Ich habe TP154 offen -- was fehlt hier eigentlich?"
 *
 * BEIDE SICHTEN RECHNEN NICHT. Sie zeigen dieselbe Antwort des Servers,
 * einmal nach Frage und einmal nach Produkt geordnet. Die zweite Sicht
 * entsteht durch Umsortieren im Browser -- eine zweite Abfrage mit
 * eigener Fachlogik waere eine zweite Wahrheit, die am Tag ihrer
 * Entstehung noch stimmt.
 *
 * WAS HIER NICHT PASSIERT
 *
 * Es wird nichts entschieden und nichts gespeichert -- auch die Auswahl
 * nicht und die Ausgabe schon gar nicht. „Beim Lieferanten beschaffbar"
 * heisst nicht „zur Beschaffung freigegeben", und ein erzeugter Text
 * heisst nicht „angefragt". Die Liste wird bei jedem Oeffnen neu
 * gerechnet; wer einen Wert pflegt, sieht sie beim naechsten Mal kuerzer.
 */
function Datenbedarf({ id, i18n, meta, onOpenArticle }: {
  id: number; i18n: I18n; meta: MetaResponse;
  onOpenArticle: (articleId: number) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [daten, setDaten] = useState<SupplierRequirements | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [sicht, setSicht] = useState<"frage" | "produkt">("frage");
  /* Hoechstens eine Gruppe aufgeklappt -- zwei offene Listen nebeneinander
     sind wieder die Wand, die dieser Bereich vermeidet. */
  const [offen, setOffen] = useState<string | null>(null);
  /*
   * DIE AUSWAHL LEBT NUR IN DER GEOEFFNETEN GRUPPE.
   *
   * Sie ist kein Zustand des Bestands, sondern eine Handbewegung: was
   * gerade in die naechste Ausgabe soll. Deshalb faellt sie weg, sobald
   * eine andere Gruppe geoeffnet, die Sicht gewechselt oder der Bereich
   * verlassen wird -- und sie ueberlebt kein Neuladen. Eine Auswahl, die
   * bliebe, waere binnen einer Woche eine Aussage ueber den
   * Beschaffungsbedarf, und die gibt es in HOMEBASE noch nicht.
   */
  const [wahl, setWahl] = useState<readonly string[]>([]);
  const [ausgabe, setAusgabe] = useState<string | null>(null);
  const [kopiert, setKopiert] = useState(false);

  useEffect(() => {
    let aktuell = true;
    setDaten(null); setError(null); setOffen(null); setWahl([]); setAusgabe(null);
    void api.supplierRequirements(id)
      .then((r) => { if (aktuell) setDaten(r); })
      .catch((e) => { if (aktuell) setError(e); });
    return () => { aktuell = false; };
  }, [id]);

  /*
   * DIE BEZEICHNUNG EINER FRAGE KOMMT AUS DEM BESTAND, NICHT VON HIER.
   *
   * Ein Merkmal heisst, wie der Katalog es nennt (`propertyLabel`, also
   * gesetzter Name vor Uebersetzung vor Code). Ein Artikelfeld benutzt
   * die Beschriftung, die die Aufmerksamkeitszone schon dafuer hat --
   * „Zolltarifnummer", „Mindestabnahme" -- statt eine zweite zu erfinden.
   * Eine Alternativgruppe hat einen eigenen Namen, weil sie kein Merkmal
   * ist: „(Laenge und Breite) oder Durchmesser" ist EINE Frage.
   */
  const frageName = (q: SupplierNeedQuestion): string => {
    if (q.subjectKind === "group") return i18n.tCode("reqGroup", q.subject, q.subject);
    if (q.subjectKind === "property") {
      const def = meta.propertyDefinitions.find((d) => d.code === q.subject);
      return propertyLabel(i18n, def ?? { code: q.subject });
    }
    return i18n.tCode("attnItem", q.subject, q.subject);
  };

  /*
   * DIE AUSGABE SPRICHT ENGLISCH -- UNABHAENGIG VON DER OBERFLAECHE.
   *
   * Sie geht an einen Lieferanten, nicht an uns. Wer HOMEBASE auf Deutsch
   * bedient, soll trotzdem keinen deutschen Text an AGC schicken. Die
   * Bezeichnung wird deshalb aus dem ENGLISCHEN Woerterbuch geholt, nicht
   * ueber `i18n` -- und bei einem Merkmal zuerst aus dem gesetzten Namen
   * `nameEn`, weil der dem Katalog gehoert und nicht der Uebersetzung.
   *
   * Uebersetzt wird dabei NICHTS: es wird nachgeschlagen, was ohnehin
   * gepflegt ist. Fehlt beides, steht der Code da -- sichtbar falsch statt
   * still erfunden.
   */
  const enWoerterbuch = en as unknown as Record<string, string | undefined>;
  const frageNameEn = (q: SupplierNeedQuestion): string => {
    if (q.subjectKind === "group") return enWoerterbuch[`reqGroup.${q.subject}`] ?? q.subject;
    if (q.subjectKind === "property") {
      const def = meta.propertyDefinitions.find((d) => d.code === q.subject);
      return def?.nameEn ?? def?.nameDe
        ?? enWoerterbuch[`prop.${q.subject}`] ?? q.subject;
    }
    return enWoerterbuch[`attnItem.${q.subject}`] ?? q.subject;
  };

  /*
   * NACH PRODUKT: dieselbe Antwort, andere Achse.
   *
   * Sortiert nach der Zahl offener Fragen -- wer ein Produkt fertig
   * machen will, faengt beim luckenreichsten an --, danach nach
   * Artikelnummer, damit die Reihenfolge bei gleicher Zahl feststeht.
   * Die Fragen INNERHALB eines Produkts behalten die Ordnung des Servers
   * (erwartet vor empfohlen, dann das Haeufigste): so steht in beiden
   * Sichten dasselbe oben.
   */
  const produkte = useMemo(() => {
    const m = new Map<number, {
      articleId: number; articleNumber: string; shortDescription: string | null;
      supplierArticleNumber: string | null; fragen: SupplierNeedQuestion[];
    }>();
    for (const q of daten?.questions ?? []) {
      for (const a of q.articles) {
        const z = m.get(a.articleId) ?? { ...a, fragen: [] };
        z.fragen.push(q);
        m.set(a.articleId, z);
      }
    }
    return [...m.values()].sort((x, y) =>
      y.fragen.length - x.fragen.length
      || x.articleNumber.localeCompare(y.articleNumber, "de", { numeric: true }));
  }, [daten]);

  /** Eine Gruppe oeffnen -- und dabei ALLES vorauswaehlen. */
  const oeffne = (key: string | null, alle: readonly string[]): void => {
    setOffen(key);
    setWahl(key === null ? [] : alle);
    setAusgabe(null); setKopiert(false);
  };
  const umschalten = (k: string): void => {
    setWahl((w) => w.includes(k) ? w.filter((x) => x !== k) : [...w, k]);
    setAusgabe(null); setKopiert(false);
  };

  const kopieren = async (text: string): Promise<void> => {
    /* `clipboard` fehlt in aelteren Umgebungen und in unsicheren
       Zusammenhaengen. Dann bleibt der Text im Feld stehen und laesst
       sich von Hand nehmen -- besser als ein Knopf, der nichts tut. */
    try { await navigator.clipboard.writeText(text); setKopiert(true); }
    catch { setKopiert(false); }
  };

  if (error !== null) return <p className="log__error">{errorText(i18n, error)}</p>;
  if (daten === null) return <p className="work__empty">{t("common.loading")}</p>;

  /*
   * ZWEI VERSCHIEDENE LEERE ZUSTAENDE, UND SIE BEDEUTEN NICHT DASSELBE.
   *
   * Kein Artikel: der Partner beliefert uns (noch) nicht -- bei einem
   * Sourcing- oder Backup-Partner voellig in Ordnung. Kein Bedarf: die
   * Produkte sind vollstaendig gepflegt. Das eine ist eine Leerstelle,
   * das andere ein Ergebnis; sie gleich zu beschriften waere eine
   * verschenkte Auskunft.
   */
  if (daten.articles === 0) return <p className="work__empty">{t("sdb.noArticles")}</p>;
  if (daten.questions.length === 0) return <p className="work__empty">{t("sdb.noNeed")}</p>;

  /*
   * DER FUSS EINER GEOEFFNETEN GRUPPE -- und die Vorschau GEHOERT DAZU.
   *
   * Hier stand die Vorschau frueher am Ende des ganzen Bereichs, hinter
   * beiden Listen. Sie wurde richtig erzeugt und war auch da -- nur
   * 4.251 Pixel und neunundachtzig Produktzeilen unter dem Knopf, der sie
   * ausgeloest hatte. Produktiv sah das aus, als geschehe nichts: unter
   * der Aktionszeile begann das naechste Produkt.
   *
   * In der Fragensicht fiel es nicht auf, weil dort nur dreizehn Zeilen
   * stehen -- knapp fuenfhundert Pixel, also gerade noch im Blick. Es war
   * derselbe Fehler, nur kuerzer.
   *
   * Jetzt steht sie INNERHALB des offenen `<li>`, direkt unter der
   * Aktionszeile. Damit ist die Zugehoerigkeit zur Gruppe nicht mehr eine
   * Frage der Bildschirmhoehe, sondern der Struktur: es gibt nur eine
   * offene Gruppe, also nur eine Vorschau, und sie kann gar nicht
   * woanders landen.
   */
  const fuss = (alle: readonly string[], text: () => string): React.JSX.Element => (
    <>
      <div className="sdb__fuss">
        <button type="button" className="btn btn--small" data-testid="sdb-alle"
          onClick={() => { setWahl(wahl.length === alle.length ? [] : alle); setAusgabe(null); }}>
          {wahl.length === alle.length ? t("sdb.selectNone") : t("sdb.selectAll")}
        </button>
        <span className="sdb__gewaehlt">{t("sdb.selected", { n: String(wahl.length) })}</span>
        <button type="button" className="btn btn--primary btn--small" data-testid="sdb-ausgabe"
          disabled={wahl.length === 0}
          onClick={() => { setAusgabe(text()); setKopiert(false); }}>
          {t("sdb.createOutput")}
        </button>
      </div>
      {/*
        * DIE VORSCHAU IST EIN TEXT, KEIN VORGANG.
        *
        * Sie entsteht aus der Auswahl, steht sichtbar unter ihr und
        * verschwindet beim naechsten Klick. Es entsteht keine
        * Datenbankzeile, kein Status und keine Historie -- wer sie
        * kopiert, hat einen Text in der Zwischenablage und sonst nichts.
        */}
      {ausgabe !== null && (
        <div className="sdb__ausgabe" data-testid="sdb-vorschau">
          <div className="sdb__ausgabekopf">
            <strong>{t("sdb.outputTitle")}</strong>
            <button type="button" className="btn btn--small" data-testid="sdb-kopieren"
              onClick={() => void kopieren(ausgabe)}>
              {kopiert ? t("sdb.copied") : t("sdb.copy")}
            </button>
          </div>
          <textarea className="sdb__ausgabetext" readOnly rows={12} value={ausgabe}
            aria-label={t("sdb.outputTitle")} />
          <p className="log__hint">{t("sdb.outputHint")}</p>
        </div>
      )}
    </>
  );

  return (
    <section className="sdb" aria-label={t("sup.datenbedarf")}>
      {/*
        * DIE ZAHLEN STEHEN NEBENEINANDER, WEIL SIE ZUSAMMEN ETWAS SAGEN.
        *
        * „778 offene Angaben" allein klingt aussichtslos. „13 offene
        * Fragen" daneben sagt, dass es ein Nachmittag ist. Der Unterschied
        * zwischen beiden Zahlen IST die Aussage dieses Bereichs.
        */}
      <div className="sdb__zahlen">
        <span className="sdb__zahl">
          <strong>{daten.articles}</strong> {t("sdb.countArticles")}
        </span>
        <span className="sdb__zahl">
          <strong>{daten.articlesWithNeed}</strong> {t("sdb.countWithNeed")}
        </span>
        <span className="sdb__zahl">
          <strong>{daten.openItems}</strong> {t("sdb.countItems")}
        </span>
        <span className="sdb__zahl sdb__zahl--stark">
          <strong>{daten.questions.length}</strong> {t("sdb.countQuestions")}
        </span>
      </div>
      <p className="log__hint">{t("sdb.hint")}</p>

      {/* Zwei Sichten auf dieselbe Rechnung -- kein Filter, keine zweite
          Abfrage, nur eine andere Achse. */}
      <div className="sdb__sicht" role="group" aria-label={t("sdb.viewAria")}>
        {(["frage", "produkt"] as const).map((v) => (
          <button key={v} type="button" data-testid={`sdb-sicht-${v}`}
            className={sicht === v ? "sdb__sichtknopf sdb__sichtknopf--on" : "sdb__sichtknopf"}
            aria-pressed={sicht === v}
            onClick={() => { setSicht(v); oeffne(null, []); }}>
            {t(v === "frage" ? "sdb.byQuestion" : "sdb.byProduct")}
          </button>
        ))}
      </div>

      {sicht === "frage" ? (
        <ul className="sdb__liste">
          {daten.questions.map((q) => {
            const key = `${q.subjectKind}:${q.subject}`;
            const auf = offen === key;
            const alle = q.articles.map((a) => String(a.articleId));
            return (
              <li key={key} className="sdb__frage">
                <button type="button" className="sdb__kopf" aria-expanded={auf}
                  data-testid="sdb-frage" data-subject={q.subject}
                  onClick={() => oeffne(auf ? null : key, alle)}>
                  <span className="sdb__pfeil" aria-hidden="true">{auf ? "▾" : "▸"}</span>
                  <span className="sdb__name">{frageName(q)}</span>
                  {/* Nur das Abweichende wird benannt: „erwartet" ist der
                      Regelfall und braucht keine Marke. */}
                  {q.weight === "empfohlen" && (
                    <span className="sdb__marke">{t("sdb.recommended")}</span>
                  )}
                  <span className="sdb__zwecke">
                    {q.purposes.map((p) => i18n.tCode("reqPurpose", p, p)).join(" · ")}
                  </span>
                  <span className="sdb__anzahl" data-testid="sdb-anzahl">
                    {q.articles.length === 1
                      ? t("sdb.oneArticle")
                      : t("sdb.nArticles", { n: String(q.articles.length) })}
                  </span>
                </button>
                {auf && (
                  <>
                    <ul className="sdb__artikel" data-testid="sdb-artikel">
                      {q.articles.map((a) => (
                        <li key={a.articleId}>
                          <label className="sdb__wahl">
                            <input type="checkbox" data-testid="sdb-haken"
                              checked={wahl.includes(String(a.articleId))}
                              onChange={() => umschalten(String(a.articleId))} />
                            <span className="sdb__artikeltext">
                              <button type="button" className="sdb__nummer"
                                onClick={() => onOpenArticle(a.articleId)}>
                                {a.articleNumber}
                              </button>
                              {a.shortDescription !== null && (
                                <span className="sdb__text">{a.shortDescription}</span>
                              )}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                    {fuss(alle, () => ausgabeFrage(
                      frageNameEn(q),
                      q.articles.filter((a) => wahl.includes(String(a.articleId)))))}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <ul className="sdb__liste">
          {produkte.map((z) => {
            const key = `a:${z.articleId}`;
            const auf = offen === key;
            const alle = z.fragen.map((q) => `${q.subjectKind}:${q.subject}`);
            return (
              <li key={key} className="sdb__frage">
                <button type="button" className="sdb__kopf" aria-expanded={auf}
                  data-testid="sdb-produkt" data-article={z.articleNumber}
                  onClick={() => oeffne(auf ? null : key, alle)}>
                  <span className="sdb__pfeil" aria-hidden="true">{auf ? "▾" : "▸"}</span>
                  <span className="sdb__name">{z.articleNumber}</span>
                  {/* Die Nummer des Lieferanten steht klein daneben -- sie
                      ist die Identitaet, unter der ER das Produkt kennt,
                      und man braucht sie beim Nachfragen. Fehlt sie, steht
                      nichts da: eine leere Marke waere ein Hinweis auf
                      nichts. */}
                  {z.supplierArticleNumber !== null && (
                    <span className="sdb__supnr" data-testid="sdb-supnr">
                      {t("sdb.supplierItemNo", { nr: z.supplierArticleNumber })}
                    </span>
                  )}
                  {z.shortDescription !== null && (
                    <span className="sdb__text sdb__text--kopf">{z.shortDescription}</span>
                  )}
                  <span className="sdb__anzahl" data-testid="sdb-anzahl">
                    {z.fragen.length === 1
                      ? t("sdb.oneQuestion")
                      : t("sdb.nQuestions", { n: String(z.fragen.length) })}
                  </span>
                </button>
                {auf && (
                  <>
                    <ul className="sdb__artikel sdb__artikel--eng" data-testid="sdb-fragen">
                      {z.fragen.map((q) => {
                        const k = `${q.subjectKind}:${q.subject}`;
                        return (
                          <li key={k}>
                            <label className="sdb__wahl">
                              <input type="checkbox" data-testid="sdb-haken"
                                checked={wahl.includes(k)} onChange={() => umschalten(k)} />
                              <span className="sdb__artikeltext">
                                <span className="sdb__nummer sdb__nummer--still">
                                  {frageName(q)}
                                </span>
                                {q.weight === "empfohlen" && (
                                  <span className="sdb__marke">{t("sdb.recommended")}</span>
                                )}
                                <span className="sdb__text">
                                  {q.purposes.map((p) => i18n.tCode("reqPurpose", p, p)).join(" · ")}
                                </span>
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                    {fuss(alle, () => ausgabeProdukt(
                      z.articleNumber, z.supplierArticleNumber,
                      z.fragen.filter((q) => wahl.includes(`${q.subjectKind}:${q.subject}`))
                        .map(frageNameEn)))}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

    </section>
  );
}

/**
 * ZWEI IDENTITAETEN FUER DASSELBE PRODUKT.
 *
 * Unsere Artikelnummer kennt der Lieferant nicht zwangslaeufig -- er
 * fuehrt es unter seiner. Beide zu nennen erspart die Rueckfrage „welches
 * Produkt meinen Sie?" und macht die Antwort zuordenbar.
 *
 * FEHLT SEINE NUMMER, WIRD NICHT GERATEN. Kein Ableiten aus unserer, kein
 * Wiederholen unserer, kein Weglassen der Zeile: `not recorded` sagt, was
 * der Fall ist. Eine erfundene Identitaet waere schlimmer als eine
 * fehlende, weil sie wie eine echte aussieht.
 */
const NICHT_GEPFLEGT = "not recorded";
const identitaet = (unsere: string, ihre: string | null): string =>
  `Our item number: ${unsere} | Your item number: ${ihre ?? NICHT_GEPFLEGT}`;

/**
 * DER ANFRAGETEXT ZU EINEM PRODUKT.
 *
 * Die Bezeichnungen stammen aus dem Merkmalskatalog, nicht aus dieser
 * Datei -- eine fest verdrahtete Merkmalsliste waere binnen eines Blocks
 * falsch. Ausgegeben wird ausschliesslich, was ausgewaehlt ist.
 */
function ausgabeProdukt(
  artikelnummer: string, lieferantennummer: string | null, fragen: readonly string[],
): string {
  return [
    /* Beim EINZELNEN Produkt stehen die beiden Nummern untereinander --
       sie sind der Kopf des Textes, nicht eine Zeile in einer Liste. */
    `Our item number: ${artikelnummer}`,
    `Your item number: ${lieferantennummer ?? NICHT_GEPFLEGT}`,
    "",
    "Please provide the following missing technical information:",
    "",
    ...fragen.map((f) => `- ${f}`),
    "",
    "Please provide the values exactly as specified in your technical documentation or datasheet.",
    "",
    'If any item is not applicable to this product, please state "not applicable".',
  ].join("\n");
}

/**
 * DER ANFRAGETEXT ZU EINER FRAGE.
 *
 * Die Bezeichnung steht in Anfuehrungszeichen und nicht im Satz.
 * „Please provide the ambient temperature for …" liest sich gut, „Please
 * provide the ip rating for …" nicht -- und „UGR" schon gar nicht. Ein
 * Katalogname ist ein Name und vertraegt keine Grammatik.
 */
function ausgabeFrage(
  frage: string,
  produkte: readonly { articleNumber: string; supplierArticleNumber: string | null }[],
): string {
  return [
    `Please provide "${frage}" for the following products:`,
    "",
    /* In der LISTE stehen beide Nummern nebeneinander -- eine Zeile je
       Produkt bleibt so eine Zeile, auch bei dreissig Produkten. */
    ...produkte.map((p) => `- ${identitaet(p.articleNumber, p.supplierArticleNumber)}`),
    "",
    "Please provide the value for each product separately.",
  ].join("\n");
}

function Produkte({ id, i18n, onOpenArticle }: {
  id: number; i18n: I18n; onOpenArticle: (articleId: number) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [items, setItems] = useState<readonly SupplierArticleRow[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [nach, setNach] = useState<Feldname>("articleNumber");
  const [auf, setAuf] = useState(true);

  useEffect(() => {
    let aktuell = true;
    void api.supplierArticles(id)
      .then((r) => { if (aktuell) setItems(r.items); })
      .catch((e) => { if (aktuell) setError(e); });
    return () => { aktuell = false; };
  }, [id]);

  /*
   * SORTIERT WIRD IM BROWSER, weil die ganze Liste ohnehin hier liegt --
   * eine Seitenabfrage je Klick waere Aufwand ohne Gewinn. Leere Werte
   * stehen IMMER hinten, in beiden Richtungen: „nicht bestimmt" ist
   * keine Angabe und gehoert nicht an den Anfang.
   */
  const sortiert = useMemo(() => {
    const liste = [...(items ?? [])];
    liste.sort((x, y) => {
      const a = x[nach], b = y[nach];
      if (a === null && b === null) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      const v = typeof a === "number" && typeof b === "number"
        ? a - b
        : String(a).localeCompare(String(b), i18n.lang, { numeric: true });
      return auf ? v : -v;
    });
    return liste;
  }, [items, nach, auf, i18n.lang]);

  const kopf = (feld: Feldname, label: string, klasse?: string) => (
    <th className={klasse}>
      <button type="button" className="sup__sort"
        aria-label={t("sup.sortBy", { feld: label })}
        onClick={() => { if (nach === feld) setAuf(!auf); else { setNach(feld); setAuf(true); } }}>
        {label}
        {/* Die Richtung steht am Kopf, nicht in einer Legende. */}
        <span className="sup__sortpfeil" aria-hidden="true">
          {nach === feld ? (auf ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );

  if (error !== null) return <p className="log__error">{errorText(i18n, error)}</p>;
  if (items === null) return <p className="work__empty">{t("common.loading")}</p>;
  /* KEIN ARTIKEL IST EIN GUELTIGER ZUSTAND -- ein Backup- oder
     Sourcing-Partner hat oft keinen. Das ist kein Fehler. */
  if (items.length === 0) return <p className="work__empty">{t("sup.noArticles")}</p>;

  return (
    <div className="sup__tablewrap">
      <table className="sup__table">
        <thead>
          <tr>
            {kopf("articleNumber", t("sup.articleNumber"))}
            {kopf("shortDescription", t("sup.articleName"))}
            {kopf("supplierArticleNumber", t("log.supplierArticleNumber"))}
            {kopf("moq", t("log.moq"), "sup__num")}
            <th className="sup__num">{t("log.orderMultiple")}</th>
            {kopf("leadTimeDays", t("log.leadTime"), "sup__num")}
            <th className="sup__num">{t("ek.price")}</th>
            <th>{t("sup.terms")}</th>
          </tr>
        </thead>
        <tbody>
          {sortiert.map((a) => (
            <tr key={a.relationId} className="sup__row" tabIndex={0} role="button"
                onClick={() => onOpenArticle(a.articleId)}
                onKeyDown={(e) => { if (e.key === "Enter") onOpenArticle(a.articleId); }}>
              {/* Der Klick fuehrt in die PRODUKTAKTE -- nicht in eine
                  Lieferantenkopie der Produktdaten. */}
              <td className="sup__kurz">{a.articleNumber}</td>
              <td className="sup__bez">
                {a.shortDescription ?? <Unknown />}
                {a.otherSuppliers.length > 0 && (
                  <span className="sup__tag" title={a.otherSuppliers.join(", ")}>
                    {t("sup.alsoFrom", { n: a.otherSuppliers.length })}
                  </span>
                )}
              </td>
              <td className="sup__supnr">{a.supplierArticleNumber ?? <Unknown />}</td>
              <td className="sup__num">{a.moq ?? <Unknown />}</td>
              <td className="sup__num">{a.orderMultiple ?? <Unknown />}</td>
              <td className="sup__num">
                {a.leadTimeDays === null ? <Unknown /> : t("log.days", { n: a.leadTimeDays })}
              </td>
              <td className="sup__num">
                {a.currentPrice === null ? <Unknown />
                  : `${Number(a.currentPrice.amount).toLocaleString(i18n.lang,
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${a.currentPrice.currency}`}
              </td>
              <td>
                {a.ownPaymentTerms
                  ? <span className="sup__tag">{t("sup.ownTerms")}</span>
                  : <span className="sup__leise">{t("sup.standardTerms")}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Notizen ─────────────────────────────────────────────────────────────────

/**
 * BEZIEHUNGSWISSEN -- eine Historie, kein Feld.
 *
 * „Haley ist genervt, dass wir zum wiederholten Male drei kleine statt
 * einer grossen Order erstellt haben" ist eine Beobachtung mit Datum und
 * Urheber. Bis 0068 stand dafuer EIN Freitextfeld am Lieferanten: wer
 * etwas ergaenzte, ueberschrieb, was jemand anderes notiert hatte.
 *
 * WAS DER BENUTZER TIPPT, IST DER INHALT. Datum und Urheber schreibt der
 * Server -- ein Datumsfeld in der Maske waere eine Einladung, eine
 * Beobachtung auf einen anderen Tag zu datieren.
 *
 * DIE NOTIZ GEHOERT DEM PARTNER ALS GANZEM. Nicht einer Zahlungs-
 * bedingung, nicht einer Bewertung, nicht einem Kontakt, nicht einem
 * Produkt -- die haben ihre eigenen Felder.
 */
function Notizen({ supplierId, notizen, i18n, mayWrite, onChanged, onError }: {
  supplierId: number; notizen: readonly SupplierNote[]; i18n: I18n;
  mayWrite: boolean; onChanged: () => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [offen, setOffen] = useState<number | "neu" | null>(null);
  const [text, setText] = useState("");
  const [speichert, setSpeichert] = useState(false);
  const [entfernt, setEntfernt] = useState<number | null>(null);
  const feld = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => { if (!mayWrite) { setOffen(null); setEntfernt(null); } }, [mayWrite]);
  useEffect(() => {
    if (offen === null) return;
    return zeigeUndFokussiere(feld.current, feld.current);
  }, [offen]);

  async function speichern(): Promise<void> {
    if (offen === null || text.trim() === "") return;
    setSpeichert(true);
    try {
      if (offen === "neu") await api.createSupplierNote(supplierId, { text });
      else await api.updateSupplierNote(supplierId, offen, { text });
      setOffen(null); setText(""); onChanged();
    } catch (e) { onError(e); } finally { setSpeichert(false); }
  }
  async function entfernen(id: number): Promise<void> {
    setSpeichert(true);
    try { await api.deleteSupplierNote(supplierId, id); setEntfernt(null); onChanged(); }
    catch (e) { onError(e); } finally { setSpeichert(false); }
  }

  /* Ein Datum, kein Zeitstempel: auf die Minute genau zu sagen, wann
     jemand etwas notiert hat, ist eine Genauigkeit ohne Aussage. */
  const tag = (iso: string): string =>
    new Date(iso).toLocaleDateString(i18n.lang,
      { day: "2-digit", month: "2-digit", year: "2-digit" });

  const maske = (
    <div className="sun__maske">
      <textarea className="log__input log__input--text sun__feld" rows={3} ref={feld}
        value={text} disabled={speichert} aria-label={t("sup.noteText")}
        placeholder={t("sup.notePlaceholder")}
        onChange={(e) => setText(e.target.value)} />
      <span className="sun__maskeaktionen">
        <button className="btn btn--primary btn--small" data-testid="sup-notizspeichern"
          disabled={speichert || text.trim() === ""} onClick={() => void speichern()}>
          {speichert ? t("props.saving") : t("log.save")}
        </button>
        <button className="btn btn--small" disabled={speichert}
          onClick={() => { setOffen(null); setText(""); }}>{t("common.cancel")}</button>
      </span>
    </div>
  );

  return (
    <section className="log__group sup__notizen" data-ziel="notizen">
      <h4 className="log__grouptitle">
        {t("sup.groupNotes")}
        {mayWrite && offen === null && (
          <button className="btn btn--small sup__plus" data-testid="sup-notizneu"
            onClick={() => { setText(""); setOffen("neu"); setEntfernt(null); }}>
            {t("sup.addNote")}
          </button>
        )}
      </h4>
      {mayWrite && offen === "neu" && maske}
      {notizen.length === 0 ? (
        <p className="log__hint">{t("sup.noNotes")}</p>
      ) : (
        <ul className="sun__liste">
          {notizen.map((n) => (
            <li key={n.id} className="sun__zeile" data-notiz={n.id}>
              {offen === n.id ? maske : (
                <>
                  <span className="sun__datum">{tag(n.createdAt)}</span>
                  <span className="sun__text">
                    {n.text}
                    {/* GEAENDERT HEISST GEAENDERT. Eine stille
                        Ueberschreibung waere eine zweite Wahrheit ueber
                        dieselbe Beobachtung; der alte Wortlaut steht im
                        Protokoll. */}
                    {n.updatedAt !== null && (
                      <span className="sun__geaendert">
                        {t("sup.noteEdited", {
                          datum: tag(n.updatedAt), wer: n.updatedBy ?? "—",
                        })}
                      </span>
                    )}
                  </span>
                  <span className="sun__autor">{n.createdBy ?? <Unknown />}</span>
                  {mayWrite && (
                    <span className="sun__aktionen">
                      <button type="button" className="subnav__pencil"
                        data-testid="sup-notizstift" data-notiz={n.id}
                        title={t("props.edit")} aria-label={t("sup.editNoteAria")}
                        disabled={speichert || offen !== null}
                        onClick={() => { setText(n.text); setOffen(n.id); setEntfernt(null); }}>
                        {PENCIL}
                      </button>
                      {entfernt === n.id ? (
                        <>
                          <span className="sup__kfrage">{t("sup.removeSure")}</span>
                          <button type="button" className="bew__weg" disabled={speichert}
                            data-testid="sup-notizweg-ja"
                            onClick={() => void entfernen(n.id)}>{t("sup.removeYes")}</button>
                          <button type="button" className="bew__weg" disabled={speichert}
                            onClick={() => setEntfernt(null)}>{t("common.cancel")}</button>
                        </>
                      ) : (
                        <button type="button" className="bew__weg" data-testid="sup-notizweg"
                          disabled={speichert || offen !== null}
                          onClick={() => setEntfernt(n.id)}>{t("sup.removeContact")}</button>
                      )}
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Finanzen ────────────────────────────────────────────────────────────────

interface BEntwurf {
  label: string; beneficiary: string; accountNumber: string; iban: string;
  bankName: string; bankAddress: string; bankCountry: string; currency: string;
  swiftBic: string; branch: string; localClearingCode: string;
  correspondentBank: string; paymentInstructions: string;
  isPrimary: boolean; active: boolean;
}

const LEERE_BANK: BEntwurf = {
  label: "", beneficiary: "", accountNumber: "", iban: "", bankName: "",
  bankAddress: "", bankCountry: "", currency: "", swiftBic: "", branch: "",
  localClearingCode: "", correspondentBank: "", paymentInstructions: "",
  isPrimary: false, active: true,
};

const ausBank = (k: SupplierBankAccount): BEntwurf => ({
  label: k.label, beneficiary: k.beneficiary, accountNumber: k.accountNumber ?? "",
  iban: k.iban ?? "", bankName: k.bankName, bankAddress: k.bankAddress ?? "",
  bankCountry: k.bankCountry ?? "", currency: k.currency,
  swiftBic: k.swiftBic ?? "", branch: k.branch ?? "",
  localClearingCode: k.localClearingCode ?? "",
  correspondentBank: k.correspondentBank ?? "",
  paymentInstructions: k.paymentInstructions ?? "",
  isPrimary: k.isPrimary, active: k.active,
});

const bankAnfrage = (e: BEntwurf): UpsertSupplierBankAccountRequest => ({
  label: e.label.trim(), beneficiary: e.beneficiary.trim(),
  accountNumber: leer(e.accountNumber), iban: leer(e.iban),
  bankName: e.bankName.trim(), bankAddress: leer(e.bankAddress),
  bankCountry: leer(e.bankCountry), currency: e.currency,
  swiftBic: leer(e.swiftBic), branch: leer(e.branch),
  localClearingCode: leer(e.localClearingCode),
  correspondentBank: leer(e.correspondentBank),
  paymentInstructions: leer(e.paymentInstructions),
  isPrimary: e.isPrimary, active: e.active,
});

/**
 * BANKVERBINDUNGEN -- null bis viele, je Waehrung eine Standardverbindung.
 *
 * WAS SIE SIND: eine STAMMANGABE des Partners. Wer eine Proforma
 * bezahlt, braucht Beneficiary, Kontonummer, Bank, SWIFT und die
 * Adresse der Bank -- und zwar die zur Rechnungswaehrung. Ein
 * chinesischer Fertiger rechnet in USD ab und hat daneben ein CNY-Konto.
 *
 * WAS SIE NICHT SIND: ein Vorgang. Hier entsteht keine Zahlung, keine
 * Bestellung und kein Beleg.
 *
 * KEIN SONDERAPPARAT (Implementierung 003). Bankdaten sind normale
 * interne Supplier-Daten: gelesen wie die uebrige Akte, geschrieben mit
 * `supplier.write`, protokolliert wie die uebrigen Stammdaten. Kein
 * Vier-Augen-Verfahren, kein zweiter Bestaetigungsweg.
 */
function Finanzen({ d, meta, i18n, mayWrite, onChanged, onError }: {
  d: SupplierDetail; meta: MetaResponse; i18n: I18n;
  mayWrite: boolean; onChanged: () => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [offen, setOffen] = useState<number | "neu" | null>(null);
  const [entwurf, setEntwurf] = useState<BEntwurf>(LEERE_BANK);
  const [speichert, setSpeichert] = useState(false);
  const [entfernt, setEntfernt] = useState<number | null>(null);
  const erstesFeld = useRef<HTMLInputElement | null>(null);
  const laender = useMemo(() => laenderListe(i18n.lang), [i18n.lang]);

  useEffect(() => { if (!mayWrite) { setOffen(null); setEntfernt(null); } }, [mayWrite]);
  useEffect(() => {
    if (offen === null) return;
    return zeigeUndFokussiere(erstesFeld.current, erstesFeld.current);
  }, [offen]);

  async function speichern(): Promise<void> {
    if (offen === null) return;
    setSpeichert(true);
    try {
      if (offen === "neu") await api.createSupplierBankAccount(d.id, bankAnfrage(entwurf));
      else await api.updateSupplierBankAccount(d.id, offen, bankAnfrage(entwurf));
      setOffen(null); setEntwurf(LEERE_BANK); onChanged();
    } catch (e) { onError(e); } finally { setSpeichert(false); }
  }
  /*
   * STANDARD SETZEN UND STILLLEGEN gehen ueber DENSELBEN Aufruf wie das
   * Bearbeiten -- er ersetzt die Entitaet. Ihm nur ein Feld mitzugeben
   * hiesse, alles andere zu leeren.
   */
  async function aendere(k: SupplierBankAccount, teil: Partial<BEntwurf>): Promise<void> {
    setSpeichert(true);
    try {
      await api.updateSupplierBankAccount(d.id, k.id,
        bankAnfrage({ ...ausBank(k), ...teil }));
      onChanged();
    } catch (e) { onError(e); } finally { setSpeichert(false); }
  }
  async function entfernen(id: number): Promise<void> {
    setSpeichert(true);
    try { await api.deleteSupplierBankAccount(d.id, id); setEntfernt(null); onChanged(); }
    catch (e) { onError(e); } finally { setSpeichert(false); }
  }

  const feld = (
    k: "label" | "beneficiary" | "accountNumber" | "iban" | "bankName" | "bankAddress"
     | "swiftBic" | "branch" | "localClearingCode" | "correspondentBank"
     | "paymentInstructions",
    label: string, klasse?: string, hinweis?: string,
  ) => (
    <label className={klasse ? `sup__kfeld ${klasse}` : "sup__kfeld"}>
      <span className="sup__klabel">{label}</span>
      <input className="log__input log__input--text" value={entwurf[k]} data-feld={k}
        aria-label={label} placeholder={hinweis ?? label} disabled={speichert}
        ref={k === "label" ? erstesFeld : undefined}
        onChange={(e) => setEntwurf({ ...entwurf, [k]: e.target.value })} />
    </label>
  );

  const maske = (
    <div className="sup__kform" data-testid="sup-bankmaske">
      {feld("label", t("sup.bankLabel"), "sup__kfeld--breit", t("sup.bankLabelHint"))}
      {feld("beneficiary", t("sup.beneficiary"), "sup__kfeld--breit")}
      {/* SEIT 0088 OPTIONAL -- die IBAN traegt die Kontonummer. Wer eine
          IBAN hat, schreibt sie nicht noch einmal auseinandergenommen
          daneben. Eines von beidem muss dastehen; das sagt der Fachkern,
          wenn beides fehlt. */}
      {feld("accountNumber", t("sup.accountNumber"), undefined, t("sup.optional"))}
      {/* IBAN IST OPTIONAL UND WIRD NIE VORAUSGESETZT -- chinesische
          Konten haben keine. */}
      {feld("iban", t("sup.iban"), undefined, t("sup.optional"))}
      <label className="sup__kfeld">
        <span className="sup__klabel">{t("sup.currency")}</span>
        <select className="log__input" value={entwurf.currency} disabled={speichert}
          aria-label={t("sup.currency")} data-testid="sup-bankwaehrung"
          onChange={(e) => setEntwurf({ ...entwurf, currency: e.target.value })}>
          <option value="">{t("sup.currencyNone")}</option>
          {meta.currencies.map((w) => <option key={w.code} value={w.code}>{w.code}</option>)}
        </select>
      </label>
      {feld("bankName", t("sup.bankName"), "sup__kfeld--breit")}
      {feld("bankAddress", t("sup.bankAddress"), "sup__kfeld--breit")}
      <label className="sup__kfeld">
        <span className="sup__klabel">{t("sup.bankCountry")}</span>
        <select className="log__input" value={entwurf.bankCountry} disabled={speichert}
          aria-label={t("sup.bankCountry")}
          onChange={(e) => setEntwurf({ ...entwurf, bankCountry: e.target.value })}>
          <option value="">{t("sup.countryNone")}</option>
          {laender.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
        </select>
      </label>
      {feld("swiftBic", t("sup.swift"))}
      {feld("branch", t("sup.branch"), undefined, t("sup.branchHint"))}
      {feld("localClearingCode", t("sup.clearing"), undefined, t("sup.clearingHint"))}
      {feld("correspondentBank", t("sup.correspondent"), "sup__kfeld--breit")}
      {feld("paymentInstructions", t("sup.payHint"), "sup__kfeld--breit")}
      <label className="sup__schalter">
        <input type="checkbox" checked={entwurf.isPrimary} disabled={speichert}
          data-testid="sup-bankstandard"
          onChange={(e) => setEntwurf({ ...entwurf, isPrimary: e.target.checked,
            active: e.target.checked ? true : entwurf.active })} />
        {t("sup.bankPrimary")}
      </label>
      <label className="sup__schalter">
        <input type="checkbox" checked={entwurf.active} disabled={speichert}
          data-testid="sup-bankaktiv"
          onChange={(e) => setEntwurf({ ...entwurf, active: e.target.checked,
            isPrimary: e.target.checked ? entwurf.isPrimary : false })} />
        {t("sup.contactActive")}
      </label>
      <span className="sup__kformaktionen">
        <button className="btn btn--primary btn--small" data-testid="sup-bankspeichern"
          disabled={speichert} onClick={() => void speichern()}>
          {speichert ? t("props.saving") : t("log.save")}
        </button>
        <button className="btn btn--small" disabled={speichert}
          onClick={() => { setOffen(null); setEntwurf(LEERE_BANK); }}>
          {t("common.cancel")}
        </button>
      </span>
    </div>
  );

  const zeile = (label: string, wert: React.ReactNode) => (
    <div className="sfi__zeile"><span className="sfi__label">{label}</span>
      <span className="sfi__wert">{wert}</span></div>
  );

  return (
    <div className="log__cluster sup__finanzen">
      <section className="log__group sup__kondgross" data-ziel="bankverbindungen">
        <h4 className="log__grouptitle">
          {t("sup.groupBank")}
          {mayWrite && offen === null && (
            <button className="btn btn--small sup__plus" data-testid="sup-bankneu"
              onClick={() => { setEntwurf(LEERE_BANK); setOffen("neu"); setEntfernt(null); }}>
              {t("sup.addBank")}
            </button>
          )}
        </h4>
        {/* KEIN LEERER ZUSTAND OHNE AUSKUNFT: dass ein Partner keine
            Bankverbindung hat, ist ein gueltiger Stand -- bei einem
            Sourcing-Kandidaten sogar der Normalfall. */}
        {d.bankAccounts.length === 0 && offen !== "neu" && (
          <p className="log__hint">{t("sup.noBank")}</p>
        )}
        {mayWrite && offen === "neu" && maske}
        {d.bankAccounts.map((k) => (
          <article key={k.id} data-bank={k.id}
            className={k.active ? "sfi__konto" : "sfi__konto sfi__konto--still"}>
            {offen === k.id ? maske : (
              <>
                <header className="sfi__kopf">
                  <span className="sfi__titel">{k.label}</span>
                  <span className="sup__tag">{k.currency}</span>
                  {k.isPrimary && (
                    <span className="sup__tag sup__tag--standard">{t("sup.bankPrimary")}</span>
                  )}
                  {!k.active && (
                    <span className="sup__tag sup__tag--aus">{t("sup.bankInactive")}</span>
                  )}
                  {mayWrite && (
                    <span className="sup__kaktionen">
                      <button type="button" className="subnav__pencil"
                        data-testid="sup-bankstift" data-bank={k.id}
                        title={t("props.edit")}
                        aria-label={t("sup.editBankAria", { name: k.label })}
                        disabled={speichert || offen !== null}
                        onClick={() => { setEntwurf(ausBank(k)); setOffen(k.id);
                          setEntfernt(null); }}>{PENCIL}</button>
                      {!k.isPrimary && k.active && (
                        <button type="button" className="bew__weg"
                          data-testid="sup-bankzustandard"
                          disabled={speichert || offen !== null}
                          onClick={() => void aendere(k, { isPrimary: true })}>
                          {t("sup.makeBankPrimary")}
                        </button>
                      )}
                      {/* STILLLEGEN STATT LOESCHEN ist der Regelweg: eine
                          Verbindung, mit der niemand mehr arbeitet, bleibt
                          sichtbar und sagt, dass es sie gab. */}
                      <button type="button" className="bew__weg"
                        data-testid="sup-bankaktivschalter"
                        disabled={speichert || offen !== null}
                        onClick={() => void aendere(k,
                          { active: !k.active, isPrimary: k.active ? false : k.isPrimary })}>
                        {k.active ? t("sup.deactivate") : t("sup.activate")}
                      </button>
                      {entfernt === k.id ? (
                        <>
                          <span className="sup__kfrage">{t("sup.removeSure")}</span>
                          <button type="button" className="bew__weg" disabled={speichert}
                            data-testid="sup-bankweg-ja"
                            onClick={() => void entfernen(k.id)}>{t("sup.removeYes")}</button>
                          <button type="button" className="bew__weg" disabled={speichert}
                            onClick={() => setEntfernt(null)}>{t("common.cancel")}</button>
                        </>
                      ) : (
                        <button type="button" className="bew__weg" data-testid="sup-bankweg"
                          disabled={speichert || offen !== null}
                          onClick={() => setEntfernt(k.id)}>{t("sup.removeContact")}</button>
                      )}
                    </span>
                  )}
                </header>
                <div className="sfi__raster">
                  {zeile(t("sup.beneficiary"), k.beneficiary)}
                  {k.accountNumber !== null && zeile(t("sup.accountNumber"),
                    <code className="sfi__nr">{k.accountNumber}</code>)}
                  {k.iban !== null && zeile(t("sup.iban"), <code className="sfi__nr">{k.iban}</code>)}
                  {zeile(t("sup.bankName"), k.bankName)}
                  {k.swiftBic !== null && zeile(t("sup.swift"),
                    <code className="sfi__nr">{k.swiftBic}</code>)}
                  {k.bankAddress !== null && zeile(t("sup.bankAddress"), k.bankAddress)}
                  {k.bankCountry !== null && zeile(t("sup.bankCountry"),
                    landName(k.bankCountry, i18n.lang))}
                  {k.branch !== null && zeile(t("sup.branch"), k.branch)}
                  {k.localClearingCode !== null && zeile(t("sup.clearing"), k.localClearingCode)}
                  {k.correspondentBank !== null && zeile(t("sup.correspondent"), k.correspondentBank)}
                  {k.paymentInstructions !== null && zeile(t("sup.payHint"), k.paymentInstructions)}
                </div>
              </>
            )}
          </article>
        ))}
        {/*
          * WAS HIER NICHT STEHT: ein Vorgang. Weder Bestellung noch
          * Proforma, weder Zahlung noch Versand -- die gehoeren in das
          * Produktions- und Logistikportal und nicht in die Akte des
          * Partners.
          */}
        <p className="log__hint">{t("sup.bankScope")}</p>
      </section>
    </div>
  );
}

// ── Konditionen ─────────────────────────────────────────────────────────────

/**
 * Konditionen -- ZWEI Bloecke, nicht drei.
 *
 * 1. RAHMENBEDINGUNGEN: alles, was allgemein fuer diesen Partner gilt --
 *    die Zahlungsbedingungen, die Lieferklausel, die Waehrung, seit wann
 *    er Partner ist und wie viele Artikel von ihm kommen.
 * 2. LIEFERANTENBEWERTUNG.
 *
 * Bis V1.2 standen die Zahlungsbedingungen als eigener, bildschirmbreiter
 * Block darueber -- und daneben ein zweiter Kasten „Rahmenbedingungen",
 * der dieselbe Frage beantwortete. Zwei Ueberschriften fuer eine Sache
 * sind eine zu viel: was allgemein gilt, gilt allgemein.
 *
 * DIE FACHLICHE LOGIK IST UNVERAENDERT: Skala 1,0-5,0 in halben
 * Schritten, NULL heisst nicht bewertet, die Gesamtnote ist gerechnet,
 * der Zahlungsplan besteht aus Stufen und ergibt 100 %, und Speichern ist
 * nicht Bestaetigen.
 *
 * ZWEI RECHTE IN EINEM BEREICH, und sie werden nicht vermischt. Der
 * Zahlungsplan und die Bewertung haengen an `logistics.write`, die
 * Lieferklausel und die Waehrung sind Stammdaten des Partners und
 * haengen an `supplier.write`. Wer nur eines hat, sieht nur dessen
 * Felder offen -- ein gemeinsamer Stift waere eine Rechteausweitung zur
 * Vereinfachung der Oberflaeche.
 */
function Konditionen({ d, meta, i18n, bearbeitet, mayTerms, mayWrite, mayConfirm,
                       vorwahl, onVerbraucht, onFertig, onReload, onError }: {
  d: SupplierDetail; meta: MetaResponse; i18n: I18n;
  bearbeitet: boolean; mayTerms: boolean; mayWrite: boolean; mayConfirm: boolean;
  vorwahl: string | null; onVerbraucht: () => void;
  onFertig: () => void;
  /** Neu laden, OHNE den Modus zu schliessen -- die Notizen haben ihren
      eigenen Schreibweg und beenden keine Konditionenbearbeitung. */
  onReload: () => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const alsAkte = { supplier: { paymentTerms: d.paymentTerms } } as never;
  const [bewertung, setBewertung] = useState<Bewertung>(() => ausBewertung(d.rating));
  const [plan, setPlan] = useState<Zahlentwurf>(() => ausZahlplan(alsAkte));
  const [rahmen, setRahmen] = useState(() => ({
    incoterm: d.incoterm ?? "", preferredCurrency: d.preferredCurrency ?? "",
  }));
  const [speichert, setSpeichert] = useState(false);
  useEffect(() => {
    setBewertung(ausBewertung(d.rating));
    setPlan(ausZahlplan({ supplier: { paymentTerms: d.paymentTerms } } as never));
    setRahmen({ incoterm: d.incoterm ?? "", preferredCurrency: d.preferredCurrency ?? "" });
  }, [d]);

  const noteOffen = !bewertungGleich(bewertung, ausBewertung(d.rating));
  const planOffen = !zahlplanGleich(plan, ausZahlplan(alsAkte));
  const rahmenOffen = rahmen.incoterm !== (d.incoterm ?? "")
                   || rahmen.preferredCurrency !== (d.preferredCurrency ?? "");
  const dirty = noteOffen || planOffen || rahmenOffen;

  async function speichern(): Promise<void> {
    setSpeichert(true);
    try {
      if (rahmenOffen) {
        await api.updateSupplier(d.id, {
          incoterm: rahmen.incoterm === "" ? null : rahmen.incoterm,
          preferredCurrency: rahmen.preferredCurrency === "" ? null : rahmen.preferredCurrency,
        });
      }
      if (noteOffen) {
        await api.setSupplierRating(d.id, Object.fromEntries(
          BEWERTUNGSFELDER.map((k) => [k, bewertung[k]])) as never);
      }
      if (planOffen) {
        if (plan.stufen.length === 0) await api.removePaymentTerms("suppliers", d.id);
        else {
          await api.setPaymentTerms("suppliers", d.id, {
            stages: plan.stufen.map((x) => ({
              sharePercent: x.anteil.trim().replace(",", "."),
              event: x.ereignis,
              eventNote: x.zusatz.trim() === "" ? null : x.zusatz.trim(),
              offsetDays: x.frist.trim() === "" ? null : Number(x.frist.trim()),
              /* Leer heisst „nicht gesagt" und wird zu `null` -- nicht
                 zu einer leeren Zeichenkette, die der Server als
                 Beschriftung aus Leerraum abweisen muesste. */
              label: x.bezeichnung.trim() === "" ? null : x.bezeichnung.trim(),
              paymentMethod: x.weg === "" ? null : (x.weg as PaymentMethod),
            })),
          });
        }
      }
      onFertig();
    } catch (e) { onError(e); } finally { setSpeichert(false); }
  }

  async function bestaetigen(): Promise<void> {
    try { await api.confirmPaymentTerms("suppliers", d.id); onFertig(); }
    catch (e) { onError(e); }
  }

  /*
   * ANGEKOMMEN -- an derselben Mechanik wie in den Stammdaten.
   *
   * Der Sprung aus der Uebersicht nennt eine fachliche Luecke; welches
   * Feld das ist, entscheidet sich erst hier. Gesprungen wird erst, wenn
   * die Bearbeitung offen ist -- vorher gibt es kein Eingabefeld, und
   * ein Fokus auf einen Lesetext waere keiner.
   */
  const kasten = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (vorwahl === null) return;
    const darf = (vorwahl === "incoterm" || vorwahl === "preferredCurrency")
      ? mayWrite : mayTerms;
    if (!bearbeitet && darf) return;
    const wurzel = kasten.current;
    if (wurzel === null) return;
    const ziel = wurzel.querySelector<HTMLElement>(`[data-ziel="${vorwahl}"]`);
    return zeigeUndFokussiere(
      ziel, ziel?.querySelector<HTMLElement>("input, select, textarea") ?? null, onVerbraucht);
  }, [vorwahl, bearbeitet, mayWrite, mayTerms, d, onVerbraucht]);

  /** Eine Auswahl aus einem Katalog des Servers -- oder der stehende Wert. */
  const katalog = (
    schluessel: "incoterm" | "preferredCurrency", label: string,
    werte: readonly { readonly code: string }[], offen: boolean, keineAngabe: string,
  ) => (
    <Zeile label={label} ziel={schluessel}>
      {offen
        ? <select className="log__input" value={rahmen[schluessel]} disabled={speichert}
            aria-label={label} data-testid={`sup-${schluessel}`}
            onChange={(e) => setRahmen({ ...rahmen, [schluessel]: e.target.value })}>
            <option value="">{keineAngabe}</option>
            {werte.map((w) => <option key={w.code} value={w.code}>{w.code}</option>)}
          </select>
        : (d[schluessel] ?? <Unknown />)}
    </Zeile>
  );

  return (
    <>
      {bearbeitet && (
        <div className="log__modus" role="status">
          <span className="log__moduslabel">{t("sup.editingTerms")}</span>
          <span className="log__modushint">{t("sup.termsHint")}</span>
          <span className="log__modusactions">
            {dirty && (
              <button className="btn btn--primary btn--small log__save" disabled={speichert}
                onClick={() => void speichern()}>
                {speichert ? t("props.saving") : t("log.save")}
              </button>
            )}
            <button className="btn btn--small log__cancel" disabled={speichert}
              onClick={() => { setBewertung(ausBewertung(d.rating));
                setPlan(ausZahlplan(alsAkte));
                setRahmen({ incoterm: d.incoterm ?? "",
                            preferredCurrency: d.preferredCurrency ?? "" });
                onFertig(); }}>
              {dirty ? t("common.cancel") : t("tk.done")}
            </button>
          </span>
        </div>
      )}

      <div className="log__cluster sup__kond" ref={kasten}>
        {/* ── 1 · RAHMENBEDINGUNGEN ───────────────────────────────── */}
        <section className="log__group sup__kondgross">
          <h4 className="log__grouptitle">{t("sup.groupFrame")}</h4>
          <div className="log__rows">
            {/* Die Zahlungsbedingung bestimmt, wann Liquiditaet
                abfliesst -- sie steht deshalb zuerst. */}
            <Zeile label={t("sup.paymentStandard")} ziel="paymentTerms">
              {d.paymentTerms === null ? <Unknown />
                : <span className="ek__plan">{zahlplanSatz(d.paymentTerms, i18n)}</span>}
            </Zeile>
            {/* Lieferklausel und Waehrung sind RAHMEN, kein Preis:
                `supplier_prices` bleibt EXW, und diese Auswahl rechnet
                keine Preiszeile um. */}
            {katalog("incoterm", t("sup.incoterm"), meta.incoterms,
                     bearbeitet && mayWrite, t("sup.incotermNone"))}
            {katalog("preferredCurrency", t("sup.currency"), meta.currencies,
                     bearbeitet && mayWrite, t("sup.currencyNone"))}
            <Zeile label={t("sup.partnerSince")}>{d.partnerSince ?? <Unknown />}</Zeile>
            <Zeile label={t("sup.status")}><StatusMarke status={d.status} i18n={i18n} /></Zeile>
            <Zeile label={t("sup.articles")}>{d.articleCount}</Zeile>
          </div>
          {d.paymentTerms !== null && (
            <p className="ek__planherkunft">
              <span className={`log__quality log__quality--${d.paymentTerms.quality}`}>
                {i18n.tCode("quality", d.paymentTerms.quality)}
              </span>
              {" "}{t("ek.termsFromSupplier")}
              {/* Speichern ist nicht Bestaetigen -- eigener Weg, eigenes Recht. */}
              {mayConfirm && d.paymentTerms.quality !== "belastbar" && (
                <button type="button" className="btn log__inline ek__planconfirm"
                  onClick={() => void bestaetigen()}>{t("log.confirm")}</button>
              )}
            </p>
          )}
          {bearbeitet && mayTerms && (
            <Zahlplaneditor entwurf={plan} ereignisse={meta.paymentEvents}
              hatStandard={d.paymentTerms !== null} speichert={speichert} i18n={i18n}
              setEntwurf={setPlan} />
          )}
          {/*
            * DIE ABWEICHUNG EINER BEZIEHUNG GEHOERT NICHT HIERHER. Sie
            * ersetzt den Standard als GANZEN Plan und wird am Artikel
            * gepflegt; hier stuende sie ein zweites Mal und waere in
            * dem Moment falsch, in dem jemand die erste aendert.
            */}
          <p className="log__hint">{t("sup.overrideHint")}</p>
        </section>

        {/* ── 2 · LIEFERANTENBEWERTUNG ────────────────────────────── */}
        <div data-ziel="rating" className="sup__bewertungkarte">
          <BewertungsBox gespeichert={d.rating} bewertung={bewertung}
            bearbeitet={bearbeitet && mayTerms} speichert={speichert} i18n={i18n}
            setWert={(k, v) => setBewertung({ ...bewertung, [k]: v })} />
        </div>
      </div>

      {/*
        * ── 3 · NOTIZEN, ueber die ganze Breite ───────────────────────
        *
        * Sie sind kein dritter Konditionenblock: sie gehoeren dem
        * Partner als Ganzem und nicht seinen Vereinbarungen. Deshalb
        * stehen sie UNTER den beiden Karten und nicht neben ihnen -- und
        * sie haengen nicht am Stift der Konditionen, sondern haben
        * ihren eigenen Weg.
        */}
      <Notizen supplierId={d.id} notizen={d.notes} i18n={i18n} mayWrite={mayWrite}
        onChanged={onReload} onError={onError} />
    </>
  );
}
